import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  Dimensions,
  Modal,
  Linking,
} from 'react-native';
import { Alert } from 'react-native';

import {
  dropdownPortalHeaderButton,
  dropdownPortalHeaderRow,
  dropdownPortalScrollArea,
  dropdownPortalTitle,
  filterSectionClearButton,
  filterSectionHeaderRow,
  filterSectionLabel,
  filterSectionPadding,
  dropdownPanelMaxHeight,
  getDropdownPortalRootStyle,
  getFilterOptionRowStyle,
  getSortOptionRowStyle,
  sortDirectionHint,
} from './viewRequestsWebPortal.styles';
import { requestDetailPathWithDiscussion } from '../components/correspondence/discussionEntry';
import {
  DiscussionRowIndicator,
  discussionMarkerLabel,
  MENTION_SLOT_WIDTH,
  MENTION_TO_GLYPH_GAP,
} from '../components/correspondence/DiscussionRowIndicator';
import { Header } from '../components/Header';
import {
  ListColumnHeader,
  ListColumnLabel,
} from '../components/ListColumnHeader';
import {
  OCRSourceSelectionModal,
  OCRSourceOption,
} from '../components/modals/OCRSourceSelectionModal';
import { ProcessingModal } from '../components/modals/ProcessingModal';
import { TextIntakeModal } from '../components/modals/TextIntakeModal';
import { toStoredPersonName } from '../utils/personName';

// Conditional import for react-dom
let createPortal: any;
if (Platform.OS === 'web') {
  try {
    // @ts-ignore
    createPortal = require('react-dom').createPortal;
  } catch (e) {
    console.warn('react-dom not available');
  }
}

// The file, not the correspondence barrel: the barrel re-exports the panel and
// the dock, which this screen never renders and whose module graph the list's
// suites need not load. The barrel exports it too, for everyone else.
import { RefreshIndicator } from '../components/RefreshIndicator';
import { RequestFailedIndicator } from '../components/RequestFailedIndicator';
import { RequestStageOrFailedBadge } from '../components/RequestStageOrFailedBadge';
import { SafeBottomArea } from '../components/SafeBottomArea';
import {
  KeyboardDismissToolbar,
  ACCESSORY_VIEW_ID,
} from '../components/shared/KeyboardDismissToolbar';
import { NewRequestSplitButton } from '../components/shared/NewRequestSplitButton';
import { ProcessingBadge } from '../components/shared/ProcessingBadge';
import { StageBadge } from '../components/StageBadge';
import { SubHeader } from '../components/SubHeader';
import { buildDocumentImportAccept } from '../constants/importFileTypes';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useApprovalGroup } from '../hooks/queries/useApprovalGroup';
import { useCreateRequestWithImport } from '../hooks/queries/useCreateRequestWithImport';
import { useJobStatus } from '../hooks/queries/useJobStatus';
import {
  useOfflineRequestsQuery,
  offlineRequestsKeys,
  useNetworkStatus,
} from '../hooks/queries/useOfflineRequestsQuery';
import {
  useRequestsPaginatedQuery,
  requestsKeys,
} from '../hooks/queries/useRequestsQuery';
import { useAuthValidation } from '../hooks/useAuthValidation';
import { useButtonPermissions } from '../hooks/useButtonPermissions';
import { useOfflineToast } from '../hooks/useOfflineToast';
import { useResponsive } from '../hooks/useResponsive';
import { useRuntimeFeatureFlags } from '../hooks/useRuntimeFeatureFlags';
import { roleService } from '../services/auth/roleService';
import { cameraService } from '../services/cameraService';
import {
  PaginatedResponse,
  PaginationParams,
} from '../services/requestsService';
import { transcriptionService } from '../services/transcriptionService';
import { colors, fontWeight, layout, spacing } from '../theme';
import { Request } from '../types';
import { RequestStage, getStageDisplayName } from '../types/stages';
import { TranscriptionJobStatus } from '../types/transcription';
import { dateFormatting } from '../utils/dateFormatting';
import { openExternalLink } from '../utils/inAppBrowser';
import {
  REQUEST_DETAIL_AWAITING_REJECT_POLL_MS,
  shouldPollRequestsListForApprovalQueue,
} from '../utils/requestDetailApprovalPoll';
import { copyRequestLinkToClipboard } from '../utils/requestFormatter';
import {
  getRequestListPrimaryLabel,
  getRequestRowAccessibilityLabel,
} from '../utils/requestListDisplay';
import {
  REQUEST_LIST_PAGE_SIZES,
  readRequestListViewState,
  writeRequestListViewState,
  type RequestListFilterOptions,
  type RequestListViewState,
} from '../utils/requestListViewState';
import { showsInternalStageDetail } from '../utils/requestorBoundary';
import { showToast } from '../utils/toast';
import {
  Search,
  Copy,
  Filter,
  ArrowUpDown,
  ChevronDown,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from './../components/icons';

import { useQueryClient } from '@tanstack/react-query';

// AWS Transcription feature imports

import type { CreateRequestWithImportData } from '../types/transcription';
import type {
  AccessibilityActionEvent,
  AccessibilityActionInfo,
} from 'react-native';

import {
  pick,
  isErrorWithCode,
  errorCodes,
  types,
} from '@react-native-documents/picker';

const isWeb = Platform.OS === 'web';

/** The native card's two actions (#1599 Story 3.2). */
const CARD_ACTION_OPEN_DISCUSSION = 'openDiscussion';
const CARD_ACTION_COPY = 'copy';
/** What an accessibility action hands a handler that stops propagation. */
const NO_PROPAGATION: { stopPropagation: () => void } = {
  stopPropagation: () => {},
};

/**
 * The order the list opens in: the ticket's "Request List is sorted by default
 * by Modified date", with that mode SHOWN as the selected one (owner, 2026-10-06).
 *
 * ⚠️ IT IS A REAL SORT FIELD NOW, NOT A SENTINEL. #1575 FR-6 first shipped this
 * as `lastChange`: a value no dropdown offered, which made the client send no
 * `orderBy` at all and let the server's own default decide. That kept "I have
 * not sorted" distinguishable from "I chose Request ID", but it also meant the
 * Sort control could never show the person what the list was actually doing.
 * The owner chose the visible mode over that distinction, so the default is now
 * the `Modified On` option itself — picking it by hand produces exactly the
 * order the list already opens in, which the sentinel could not promise.
 *
 * The server pairs it with `updated_at` as a tie-break, because the column is a
 * date with no time; see `request.repository.findWithPagination`.
 */
const DEFAULT_SORT: { field: string; direction: 'asc' | 'desc' } = {
  field: 'sysModifiedOn',
  direction: 'desc',
};

const DEFAULT_PAGE_SIZE = 20;

/**
 * The view someone who has never touched the controls sees, and the fallback each field of
 * a retained view (#1532) is validated against. Declared once so the "clear" handlers and
 * the retention store cannot disagree about what "no filters, default order" means.
 */
const DEFAULT_LIST_VIEW_STATE: RequestListViewState = {
  searchText: '',
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
  filterOptions: {
    region: '',
    modifiedOn: '',
    modifiedBy: '',
    stage: '',
    /** #1575: '' (everything), 'unread' or 'read'. Decided per viewer, server-side. */
    readStatus: '',
    /**
     * #1640: '' (everything), 'with' or 'without'. Like `readStatus` it is decided
     * per viewer server-side — "has a discussion" means one THIS person may read,
     * which is the same question the row's discussion marker answers.
     */
    discussionStatus: '',
  },
  sortOptions: { ...DEFAULT_SORT },
};

const defaultFilterOptions = (): RequestListFilterOptions => ({
  ...DEFAULT_LIST_VIEW_STATE.filterOptions,
});

/**
 * The Discussion filter's options (#1640), shared by the native modal and the web
 * portal so the two copies of the section cannot drift -- the same reason
 * `stageFilterOptions` is built once.
 *
 * ⚠️ THE VALUES ARE THE WIRE FORMAT. `''` is "do not filter" and is never sent;
 * `with` and `without` are what `parseDiscussionFilter` accepts server-side, and
 * anything else there is ignored rather than returning an empty list. Renaming a
 * value is a server change too.
 */
const DISCUSSION_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'with', label: 'With discussion' },
  { value: 'without', label: 'Without discussion' },
];

/**
 * Which columns the web TABLE shows (UX decision, Sally, 2026-09-11).
 *
 * Nine columns at their minimum widths need ~1016 px of row; the card offers
 * 926 px at a 998 px window, so between the 768 px table breakpoint and
 * ~1100 px the last cell (the discussion marker and Copy) spilled past the
 * card. A data table gives up columns before it gives up the signal: `mid`
 * (900-1099) drops Created Date and Modified By, `narrow` (768-899) also
 * drops Requestor Name. Modified On stays because this list is about what
 * changed; Region stays because it is how people filter. Created Date and
 * Requestor Name remain in the Sort control; Modified By never was sortable
 * and below 1100 px is read on the request itself (accepted, Sally
 * 2026-09-11). Header and rows read ONE tier value, so they
 * can never disagree, and a collapsed column leaves the flex row entirely
 * (never zero width). Below 768 px the card renderer is untouched.
 */
type TableColumnTier = 'wide' | 'mid' | 'narrow';

/** Icon size of the two row actions (the discussion marker and Copy). */
const ROW_ACTION_GLYPH = 16;
/**
 * The padding of the two hosts the discussion marker is dropped into, and the
 * ONLY thing keeping the unread dot's overhang clear of a rounded edge Android
 * clips at.
 *
 * ⚠️ EXPORTED SO THE GUARD CAN STOP RETYPING THEM (review, 2026-09-23). These
 * were literals here and re-declared as a literal `6` in
 * `view-requests-actions-column-floor.test.ts`, so lowering one of them left
 * every assertion green while the dot moved onto the border -- the "green test
 * at the wrong level" shape `correspondence.md` records. The margin is ONE
 * POINT on the card (`DOT_OVERHANG_RIGHT` is 5), so this is not theoretical.
 */
export const TABLE_ACTION_PADDING = spacing.sm;
export const CARD_ACTION_PADDING = 6;
/**
 * The actions cell's floor: the marker (padding, glyph, padding) beside Copy
 * (padding, glyph, padding), with `actionButtons`' gap between them.
 * 8+16+8 + 8 + 8+16+8 = 72, plus the mention `@`'s 12 + 2 = 86 (#1599 Story 1.5).
 *
 * ⚠️ WAS 88 UNTIL 2026-09-23, and the 16 px it lost is the whole of the change:
 * the unread dot used to sit IN FLOW after the glyph (a 4 px gap plus a 12 px
 * dot), so the marker was the wider of the two buttons and the floor had to
 * hold it. The dot now overhangs the glyph's corner, absolutely positioned and
 * out of flow, so it contributes NOTHING to this width and the two buttons are
 * the same size. `ROW_ACTION_UNREAD_DOT` and the imports of `UNREAD_DOT_SIZE`
 * and `GLYPH_TO_DOT_GAP` went with it.
 *
 * ⚠️ CONTENT EQUALS THE FLOOR EXACTLY. There is no slack, and that is deliberate
 * (a derived floor, not a taste choice, the way `spacing.formMaxWidth` is), and
 * it is still true at 72 — which is why the floor moved rather than keeping 88
 * and quietly gaining 16 px this comment would have denied having.
 * `view-requests-actions-column-floor.test.ts` recomputes it from the same
 * constants and additionally guards the overhang against the smaller host
 * padding, so growing a glyph, changing either host's padding, or pulling the
 * dot further out reddens instead of spilling the cell over the Stage badge or
 * clipping the dot on Android.
 *
 * ⚠️ IT DOES NOT CATCH A THIRD ROW ACTION (corrected at review 2026-09-23; the
 * previous wording claimed it did). That test does not render -- adding another
 * `actionButton` beside Copy reddens nothing until somebody edits the
 * derivation. `TABLE_ACTION_PADDING` is used here rather than `spacing.sm`
 * directly so that raising the real padding moves this number with it; they are
 * equal today and were silently uncoupled until the review.
 *
 * It replaced a hand-set `100`, which had 68 px of accidental slack when Copy
 * was alone here.
 */
export const ACTIONS_COLUMN_MIN_WIDTH =
  TABLE_ACTION_PADDING * 2 +
  ROW_ACTION_GLYPH +
  // #1599 Story 1.5 (owner ruling 2026-10-22): the mention `@` sits IN FLOW
  // left of the marker's glyph, so the floor holds the LIT marker, the widest
  // it can be. 72 -> 86. Right-aligned cell: the glyph and Copy do not move.
  MENTION_SLOT_WIDTH +
  MENTION_TO_GLYPH_GAP +
  spacing.sm +
  (TABLE_ACTION_PADDING * 2 + ROW_ACTION_GLYPH);

const tableColumnTierFor = (width: number): TableColumnTier =>
  width >= 1100 ? 'wide' : width >= 900 ? 'mid' : 'narrow';

const vrLayout = layout.viewRequests;

const REQUEST_LIST_CALENDAR = {
  locale: 'en-US',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
} as const;

/** Written calendar date. Not parsed as UTC midnight. */
const formatWrittenCalendarDate = (value: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(utcNoon);
};

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  const instant = dateFormatting.format(dateString, REQUEST_LIST_CALENDAR);
  if (instant) return instant;
  const written = formatWrittenCalendarDate(dateString);
  if (written) return written;
  return 'N/A';
};

interface ViewRequestsProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

export const ViewRequests: React.FC<ViewRequestsProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const userEmail = user?.email;

  /**
   * #1532 -- the list's filters, order, page size and page, as they were when this person
   * last left the screen. Read synchronously (see `requestListViewState`) so the first
   * render, and so the first paginated request, already carry them: restoring after mount
   * would fetch page 1 of the unfiltered list first and visibly snap.
   *
   * `App.tsx` keys this screen on the user's email, so an identity change -- including
   * starting or ending an impersonation session -- remounts it and re-reads that person's
   * own view rather than inheriting the previous one.
   */
  const [retainedViewState] = useState<RequestListViewState>(() =>
    readRequestListViewState(userEmail, DEFAULT_LIST_VIEW_STATE),
  );

  const [searchText, setSearchText] = useState(retainedViewState.searchText);
  const [pageSize, setPageSize] = useState(retainedViewState.pageSize);
  const [currentPage, setCurrentPage] = useState(retainedViewState.currentPage);
  const usePagination = true;
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get('window').width,
  );
  const { isSmallScreen } = useResponsive();
  // Internal-only "Configuration" label (PRD FR7): shown on the stage badges and on the
  // CONFIGURATION filter option. Keyed on the viewer's `External Requestor` role, behind a
  // roles-present guard so an identity whose roles never loaded hides it (PRD FR28).
  const showInternalDetail = showsInternalStageDetail(user);

  /**
   * Stage filter options, shared by the compact and wide lists so the two cannot drift.
   *
   * An external viewer gets **one** working-stage option, not two both reading
   * "In Progress" — a dropdown offering the same word twice reads as a defect, and the
   * "Configuration" label that told them apart is internal-only by design (PRD FR7).
   *
   * Accepted residual (approved 2026-08-05, recorded in `deferred-work.md`): an external
   * user's CONFIGURATION requests are not reachable from the remaining "In Progress"
   * option, only from "All". Collapsing the two stages onto one option would need a
   * multi-value stage parameter or a server-side alias, which this cycle does not build.
   */
  const stageFilterOptions = useMemo(
    () => [
      { value: 'DRAFT', label: getStageDisplayName(RequestStage.DRAFT) },
      {
        value: 'AWAITING_APPROVAL',
        label: getStageDisplayName(RequestStage.AWAITING_APPROVAL),
      },
      {
        value: 'SITE_ASSESSMENT',
        label: getStageDisplayName(RequestStage.SITE_ASSESSMENT),
      },
      ...(showInternalDetail
        ? [
            {
              value: 'CONFIGURATION',
              label: getStageDisplayName(RequestStage.CONFIGURATION, true),
            },
          ]
        : []),
      { value: 'CLOSED', label: getStageDisplayName(RequestStage.CLOSED) },
    ],
    [showInternalDetail],
  );
  const { isImpersonating, mode } = useImpersonation();
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';
  const queryClient = useQueryClient();
  const isOnline = useNetworkStatus();
  const { data: approvalGroup, isLoading: approvalGroupLoading } =
    useApprovalGroup();
  const partnerApprovalsActive =
    approvalGroup?.enabled === true && (approvalGroup.members?.length ?? 0) > 0;
  const isPartnerViewer = roleService.isExternalUser(user ?? null);
  const approvalGatePending = isPartnerViewer && approvalGroupLoading;
  const viewerIsLiveApprovalGroupMember = useMemo(() => {
    if (!partnerApprovalsActive || approvalGatePending || !user) {
      return false;
    }
    const uid = user.userId;
    if (uid == null) {
      return false;
    }
    return approvalGroup?.members?.some(m => m.userId === uid) ?? false;
  }, [
    partnerApprovalsActive,
    approvalGatePending,
    user,
    approvalGroup?.members,
  ]);
  const canViewDraftStageRows =
    !!user &&
    (roleService.isRequestor(user) ||
      roleService.isExternalRequestor(user) ||
      roleService.isITAdministrator(user));
  const canViewAwaitingApprovalRows =
    canViewDraftStageRows || viewerIsLiveApprovalGroupMember;

  // Phase 2.2: Enable background session validation on web
  useAuthValidation();

  // Filter and sort state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
  const [filterOptions, setFilterOptions] = useState<RequestListFilterOptions>(
    retainedViewState.filterOptions,
  );

  // Clear stage filters the viewer cannot use (approvers may filter Awaiting Approval)
  React.useEffect(() => {
    if (!user) {
      return;
    }
    if (filterOptions?.stage === 'DRAFT' && !canViewDraftStageRows) {
      setFilterOptions(prev => ({
        ...prev,
        stage: '',
      }));
      return;
    }
    if (
      filterOptions?.stage === 'AWAITING_APPROVAL' &&
      !canViewAwaitingApprovalRows
    ) {
      setFilterOptions(prev => ({
        ...prev,
        stage: '',
      }));
    }
  }, [
    user,
    filterOptions?.stage,
    canViewDraftStageRows,
    canViewAwaitingApprovalRows,
  ]);
  const [sortOptions, setSortOptions] = useState(retainedViewState.sortOptions);
  /**
   * Has the person chosen an order? The Sort button's tint, its label and the
   * Reset button all mean this one thing, so they read it from one place
   * instead of each re-stating the default (#1575 FR-6 moved that default).
   */
  const sortIsDefault =
    sortOptions.field === DEFAULT_SORT.field &&
    sortOptions.direction === DEFAULT_SORT.direction;

  /**
   * Has the person narrowed the list? The Filter button's tint, its "(Active)" label, the
   * toolbar Reset and the "(filtered)" count all mean this one thing, so they read it from
   * here rather than each re-stating the list of filters -- the same reason `sortIsDefault`
   * exists.
   *
   * **Read Status counts** (#1532 review, Kiran issue 1). It filters the list exactly like the
   * other three and is sent with the query, but all five sites were written before #1575 added
   * it, so picking Unread alone left the button grey and Reset hidden -- the list was filtered
   * and nothing said so. **Discussion counts for the same reason** (#1640) -- a new per-viewer
   * filter has to be added HERE as well as to the query, or it repeats that bug.
   */
  const filtersAreActive = Boolean(
    filterOptions.region ||
      filterOptions.stage ||
      filterOptions.modifiedOn ||
      filterOptions.readStatus ||
      filterOptions.discussionStatus,
  );

  /**
   * #1532 -- mirror the live controls into the retention store on every change, so the next
   * mount of this screen (Back from a request, or a web page reload) starts where the person
   * left off. One effect rather than a wrapper around each setter: the "clear" handlers, the
   * stage-filter correction below and the page clamp all flow through here for free.
   *
   * Skipped until the identity is known -- the first render of a cold load has no user yet
   * (the paginated query is disabled for the same reason), and writing then would file the
   * default view under an anonymous key nothing ever reads.
   */
  useEffect(() => {
    if (!userEmail) {
      return;
    }
    writeRequestListViewState(userEmail, {
      searchText,
      pageSize,
      currentPage,
      filterOptions,
      sortOptions,
    });
  }, [
    userEmail,
    searchText,
    pageSize,
    currentPage,
    filterOptions,
    sortOptions,
  ]);

  // AWS Transcription feature state
  const [processingModalVisible, setProcessingModalVisible] = useState(false);
  // Text intake keeps the completion modal open until dismissed (autoClose=false);
  // audio/document imports keep the default 30s auto-close (autoClose=true).
  const [processingModalAutoClose, setProcessingModalAutoClose] =
    useState(true);
  const [processingRequestId, setProcessingRequestId] = useState<number>();
  const [processingFileName, setProcessingFileName] = useState<string>();

  // Text intake state (Create Request from Text)
  const [textIntakeModalVisible, setTextIntakeModalVisible] = useState(false);
  const [textIntakeSubmitting, setTextIntakeSubmitting] = useState(false);
  // Gate the Create-from-Text option on the DRAFT Button - Text Intake matrix.
  const draftButtonPermissions = useButtonPermissions('DRAFT');
  const { textIntake: textIntakeEnabled } = useRuntimeFeatureFlags();

  // OCR source selection modal state (mobile only)
  const [ocrSourceModalVisible, setOcrSourceModalVisible] = useState(false);

  // Transcription mutation
  const createWithImportMutation = useCreateRequestWithImport();

  // Background polling for processing requests (continues even when modal is closed)
  const { data: backgroundJobStatus } = useJobStatus(processingRequestId, {
    enabled: !!processingRequestId, // Continue polling even when modal is closed
    refetchInterval: 5000, // Poll every 5 seconds
  });

  // Invalidate queries when processing completes or fails (background polling)
  useEffect(() => {
    if (
      processingRequestId &&
      backgroundJobStatus &&
      (backgroundJobStatus.status === TranscriptionJobStatus.COMPLETED ||
        backgroundJobStatus.status === TranscriptionJobStatus.FAILED)
    ) {
      // Invalidate request list queries to refresh the list
      queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requestsKeys.all });

      // Invalidate specific request detail query
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.detail(String(processingRequestId)),
      });
      queryClient.invalidateQueries({
        queryKey: requestsKeys.detail(String(processingRequestId)),
      });

      // Clear processing request ID and close modal after 12 seconds to allow user to read the result
      const timer = setTimeout(() => {
        setProcessingRequestId(undefined);
        setProcessingModalVisible(false);
      }, 12000); // 12 seconds (12000ms) - user requested 10-15 seconds

      return () => clearTimeout(timer);
    }
  }, [backgroundJobStatus?.status, processingRequestId, queryClient]);

  // Helper function to handle file selection and create request
  const handleFileSelectionAndCreate = async (file: {
    uri: string;
    name: string;
    type: string;
    size: number;
    webFile?: File;
  }) => {
    if (!user) {
      showToast.error('User not authenticated');
      return;
    }

    // Set customer name and region to [BLANK] for audio/PDF imports
    const data: CreateRequestWithImportData = {
      customerName: '[BLANK]',
      region: '[BLANK]',
      requestorName: toStoredPersonName(user.name),
      requestorEmail: user.email || '',
      file: {
        uri: file.uri,
        name: file.name,
        type: file.type,
        size: file.size,
        webFile: file.webFile,
      },
    };

    try {
      const response = await createWithImportMutation.mutateAsync(data);

      // Show processing modal
      setProcessingRequestId(response.requestId);
      setProcessingFileName(data.file.name);
      // An uploaded .txt is a text-intake job too (same file_type='text' pipeline
      // as a paste), so hold the completion modal like a paste. Audio/PDF/image
      // keep the default 30s auto-close.
      const isTextUpload =
        data.file.type === 'text/plain' ||
        !!data.file.name?.toLowerCase().endsWith('.txt');
      setProcessingModalAutoClose(!isTextUpload);
      setProcessingModalVisible(true);

      // Refresh requests list to show new PROCESSING request immediately
      refetch();
      if (usePagination) {
        refetchPaginated();
      }
    } catch (error) {
      // Alert.alert is a no-op on react-native-web, so import failures were silent
      // on the web (a rejected upload showed nothing). Surface the actual error via
      // the toast system instead so the user knows it failed and why.
      console.error('Failed to create request with import:', error);
      showToast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create request. Please try again.',
      );
    }
  };

  // Open the Create-from-Text modal
  const handleImportText = () => {
    setTextIntakeModalVisible(true);
  };

  // Submit pasted text -> create a request from it (no file upload)
  const handleTextIntakeSubmit = async (
    text: string,
    options: { emailThread?: boolean } = {},
  ) => {
    if (!user) {
      showToast.error('User not authenticated');
      return;
    }
    setTextIntakeSubmitting(true);
    try {
      const response = await transcriptionService.createRequestFromText({
        customerName: '[BLANK]',
        region: '[BLANK]',
        requestorName: toStoredPersonName(user.name),
        requestorEmail: user.email || '',
        text,
        emailThread: options.emailThread,
      });
      setTextIntakeModalVisible(false);
      setProcessingRequestId(response.requestId);
      setProcessingFileName('Pasted text');
      setProcessingModalAutoClose(false); // text intake: hold until user dismisses
      setProcessingModalVisible(true);
      refetch();
      if (usePagination) {
        refetchPaginated();
      }
    } catch (error) {
      // Alert.alert is a no-op on react-native-web, so a failed create-from-text
      // was silent on the web (modal open, spinner stopped, no error). Surface it
      // via the toast system instead — same fix as the document-import path.
      console.error('Failed to create request from text:', error);
      showToast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create request from text.',
      );
    } finally {
      setTextIntakeSubmitting(false);
    }
  };

  // Handle web file selection
  const handleWebFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    mode: 'audio' | 'document',
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create a data URL for the file
      const reader = new FileReader();
      reader.onloadend = () => {
        handleFileSelectionAndCreate({
          uri: reader.result as string,
          name: file.name,
          type: file.type,
          size: file.size,
          webFile: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // AWS Transcription handlers - now open file picker directly
  const handleImportFile = () => {
    if (Platform.OS === 'web') {
      // For web, create a temporary file input and trigger it
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = buildDocumentImportAccept(textIntakeEnabled);
      input.style.display = 'none';
      input.setAttribute('data-testid', 'import-document-file-input');
      input.onchange = e => {
        handleWebFileChange(e as any, 'document');
        document.body.removeChild(input);
      };
      document.body.appendChild(input);
      input.click();
    } else {
      // For native, show the OCR source selection modal
      setOcrSourceModalVisible(true);
    }
  };

  // Handle OCR source selection from modal (mobile only)
  const handleOCRSourceSelect = async (option: OCRSourceOption) => {
    try {
      switch (option) {
        case 'camera': {
          // Take a photo with the camera
          const photo = await cameraService.takePhoto();
          if (photo) {
            handleFileSelectionAndCreate({
              uri: (photo.file as any).uri || '',
              name: photo.name,
              type: photo.type,
              size: photo.size,
            });
          }
          break;
        }
        case 'library': {
          // Choose from photo library (single selection only for OCR)
          const photos = await cameraService.chooseFromLibrary('photo', 1);
          if (photos && photos.length > 0) {
            const photo = photos[0];
            handleFileSelectionAndCreate({
              uri: (photo.file as any).uri || '',
              name: photo.name,
              type: photo.type,
              size: photo.size,
            });
          }
          break;
        }
        case 'files': {
          // Use document picker for PDFs and images
          pick({
            type: [types.pdf, types.images, types.plainText],
          })
            .then(result => {
              if (result && result[0]) {
                handleFileSelectionAndCreate({
                  uri: result[0].uri,
                  name: result[0].name || 'unknown',
                  type: result[0].type || 'application/octet-stream',
                  size: result[0].size || 0,
                });
              }
            })
            .catch(error => {
              if (
                !(
                  isErrorWithCode(error) &&
                  error.code === errorCodes.OPERATION_CANCELED
                )
              ) {
                console.error('Error selecting file:', error);
                Alert.alert(
                  'Error',
                  'Failed to select file. Please try again.',
                );
              }
            });
          break;
        }
      }
    } catch (error) {
      console.error('Error handling OCR source selection:', error);
      Alert.alert(
        'Error',
        'Failed to capture or select image. Please try again.',
      );
    }
  };

  const handleImportAudio = () => {
    if (Platform.OS === 'web') {
      // For web, create a temporary file input and trigger it
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'audio/*';
      input.style.display = 'none';
      input.setAttribute('data-testid', 'import-audio-file-input');
      input.onchange = e => {
        handleWebFileChange(e as any, 'audio');
        document.body.removeChild(input);
      };
      document.body.appendChild(input);
      input.click();
    } else {
      // For native, use document picker
      pick({
        type: [types.audio],
      })
        .then(result => {
          if (result && result[0]) {
            handleFileSelectionAndCreate({
              uri: result[0].uri,
              name: result[0].name || 'unknown',
              type: result[0].type || 'application/octet-stream',
              size: result[0].size || 0,
            });
          }
        })
        .catch(error => {
          if (
            !(
              isErrorWithCode(error) &&
              error.code === errorCodes.OPERATION_CANCELED
            )
          ) {
            console.error('Error selecting audio:', error);
            Alert.alert(
              'Error',
              'Failed to select audio file. Please try again.',
            );
          }
        });
    }
  };

  const handleNavigateToRequest = (requestId: number) => {
    navigate(`/request-detail?id=${requestId}`);
  };

  // Dropdown positioning refs and state
  const filterButtonRef = useRef<any>(null);
  const sortButtonRef = useRef<any>(null);
  /**
   * `maxHeight` rides along with the position because it is measured at the
   * same moment and from the same rect: how much room is left BELOW the button
   * (#1640). A constant panel height hid "Without discussion", and Region and
   * Stage with it — see `dropdownPanelMaxHeight`.
   */
  const [filterDropdownPosition, setFilterDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: undefined as number | undefined,
  });
  const [sortDropdownPosition, setSortDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: undefined as number | undefined,
  });
  // Dynamic breakpoint system for responsive web
  const isTablet = screenWidth >= 768;
  const isLargeMobile = screenWidth >= 414;
  const isMobileLandscape = screenWidth >= 480 && screenWidth < 768;

  // Determine rendering mode
  const shouldShowTable = isWeb && screenWidth >= 768;
  // One tier for the header AND every row (see `TableColumnTier`).
  const tableColumnTier = tableColumnTierFor(screenWidth);
  const showsCreatedAndModifiedBy = tableColumnTier === 'wide';
  const showsRequestorColumn = tableColumnTier !== 'narrow';
  const headerColumnGap =
    shouldShowTable && tableColumnTier !== 'wide'
      ? spacing.sm
      : vrLayout.tableColumnGap;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });

    return () => subscription?.remove();
  }, []);

  // Debounced search text to prevent excessive API calls
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchText]);

  /**
   * Back to page 1 when the search term actually changes.
   *
   * The ref makes this skip the mount run: a bare `[debouncedSearchText]` effect fires once
   * on every mount, which would throw away the page a returning person had (#1532) before
   * the first query even went out.
   */
  const previousDebouncedSearchRef = useRef(debouncedSearchText);
  useEffect(() => {
    if (previousDebouncedSearchRef.current === debouncedSearchText) {
      return;
    }
    previousDebouncedSearchRef.current = debouncedSearchText;
    setCurrentPage(1);
  }, [debouncedSearchText]);

  // Memoize pagination parameters to prevent unnecessary re-renders
  const paginationParams: PaginationParams = useMemo(
    () => ({
      page: currentPage,
      pageSize,
      // The default is a real column now (see DEFAULT_SORT), so it is named on
      // the wire like any other choice and the server applies its Modified On
      // tie-break to it.
      orderBy:
        sortOptions.field === 'dateCreated'
          ? 'created_at'
          : sortOptions.field === 'sysModifiedOn'
          ? 'sys_modified_on'
          : sortOptions.field === 'customerName'
          ? 'customer_name'
          : sortOptions.field === 'requestorName'
          ? 'requestor_name'
          : sortOptions.field === 'id'
          ? 'request_id'
          : sortOptions.field === 'region'
          ? 'region'
          : sortOptions.field === 'stage'
          ? 'sys_request_stage'
          : sortOptions.field,
      orderDirection: sortOptions.direction.toUpperCase() as 'ASC' | 'DESC',
      searchText: debouncedSearchText || undefined,
      region: filterOptions.region || undefined,
      stage: filterOptions.stage || undefined,
      modifiedOn: filterOptions.modifiedOn || undefined,
      readStatus: filterOptions.readStatus || undefined,
      discussionStatus: filterOptions.discussionStatus || undefined,
      userRoles: user?.roles || [],
    }),
    [
      currentPage,
      pageSize,
      sortOptions.field,
      sortOptions.direction,
      debouncedSearchText,
      filterOptions.region,
      filterOptions.stage,
      filterOptions.modifiedOn,
      filterOptions.readStatus,
      filterOptions.discussionStatus,
      user?.roles,
    ],
  );

  // Use either paginated or non-paginated query
  const {
    data: requests = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useOfflineRequestsQuery();
  const {
    data: paginatedData,
    isLoading: isPaginatedLoading,
    error: paginatedError,
    refetch: refetchPaginated,
    isFetching: isPaginatedFetching,
  } = useRequestsPaginatedQuery(paginationParams, {
    /**
     * #1532 review, Kiran issue 2 -- Read Status is the only filter whose value THIS APP
     * changes while the person is away from the list: opening a request marks it read, and
     * #1575's read marker deliberately patches the cached row's flag instead of invalidating
     * (`clearCachedUnseenChanges` explains why -- offline there is nothing to invalidate into,
     * and a replayed read would be handed the bold row straight back).
     *
     * Now that the view is retained, coming back to an "Unread" list would therefore show the
     * request just opened, sitting there marked Read, for the rest of the 30s window. Zero
     * stale time makes the mount refetch, so the row leaves with the filter still applied.
     *
     * **Discussion is the second such filter** (#1640): posting the first message of a
     * conversation is something the person does INSIDE a request, so a "Without discussion"
     * list they came back to would still be holding the request they just started talking on.
     *
     * Scoped to these two on purpose: Region / Stage / Modified On cannot be invalidated
     * by opening a request, so they keep the cheap cached mount. Offline the refetch just
     * fails and the stale row stays -- the offline-first answer, and no worse than before.
     */
    staleTime:
      filterOptions.readStatus || filterOptions.discussionStatus
        ? 0
        : undefined,
  });

  /**
   * Typed view of the server's page envelope. The query's own `data` widens to `{}` here
   * (its auth-aware `placeholderData` can return `undefined`), so the page clamp below reads
   * the counts through this instead of re-deriving them untyped.
   */
  const paginationMeta = (
    paginatedData as PaginatedResponse<Request> | undefined
  )?.pagination;

  // Choose which data to use based on pagination mode
  // When using pagination, merge offline requests (from useOfflineRequestsQuery) with paginated server data
  // Key Strategy: Offline requests use 'o-' prefix (e.g., 'o-123'), server requests use numeric IDs
  // This ensures uniqueness when merging offline and server data for React keys
  const offlineRequests = requests.filter(r => r.id && r.id.startsWith('o-'));
  const cachedServerRequests = requests.filter(
    r => r.id && !r.id.startsWith('o-'),
  );
  // `Boolean(x, y)` ignores `y`: readStatus was passed as a second ARGUMENT, so an
  // Unread-only filter counted as "no filters" and fell back to the unfiltered cached rows.
  const hasActiveListFilters = Boolean(debouncedSearchText) || filtersAreActive;
  const paginatedServerRequests =
    paginatedData != null
      ? paginatedData.data ?? []
      : hasActiveListFilters
      ? []
      : cachedServerRequests;
  const actualRequests = usePagination
    ? [...offlineRequests, ...paginatedServerRequests]
    : requests || [];
  const actualIsLoading = usePagination ? isPaginatedLoading : isLoading;
  const actualError = usePagination ? paginatedError : error;
  const actualRefetch = usePagination ? refetchPaginated : refetch;
  const actualIsFetching = usePagination ? isPaginatedFetching : isFetching;

  const pollListForApprovalQueue = shouldPollRequestsListForApprovalQueue({
    isOnline,
    partnerApprovalsActive,
    viewerIsLiveApprovalGroupMember,
  });

  // Approvers (incl. impersonation) do not get list invalidation when someone else submits.
  useEffect(() => {
    if (!pollListForApprovalQueue) {
      return;
    }
    void actualRefetch();
    const intervalId = setInterval(() => {
      void actualRefetch();
    }, REQUEST_DETAIL_AWAITING_REJECT_POLL_MS);
    return () => clearInterval(intervalId);
  }, [pollListForApprovalQueue, actualRefetch]);

  // Override error state if we have data - this handles cases where an error occurred initially
  // but subsequent requests succeeded and we have valid data to display
  const hasValidData = actualRequests && actualRequests.length > 0;
  const shouldShowError = actualError && !hasValidData;

  // Show loading state when fetching paginated data
  const isDataLoading = actualIsLoading || actualIsFetching;

  // Removed forced refresh - the query should now work properly with networkMode: 'always'
  useOfflineToast(); // Initialize offline toast functionality

  /**
   * #1532 -- a retained page can outrun the result set: requests closed or deleted since the
   * person left, or a stage filter the stage-permission effect above has since cleared. Land
   * on the last page that has rows rather than an empty list under "Page 7 of 3".
   *
   * `totalPages` is 0 for an empty result, hence the floor of 1. This settles in one step:
   * re-querying the clamped page returns the same `totalPages`, so the condition is then
   * false. It cannot misfire on a filter change either -- those reset the page to 1.
   */
  const totalPages = paginationMeta?.totalPages;
  useEffect(() => {
    if (typeof totalPages !== 'number') {
      return;
    }
    const lastPage = Math.max(1, totalPages);
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [totalPages, currentPage]);

  // Helper function to check if user can view a request based on its stage
  const canViewRequest = (request: Request): boolean => {
    // Ensure request exists
    if (!request) return false;

    const isDraftStage =
      request.stage === 'DRAFT' || request.sysRequestStage === 'DRAFT';
    const isAwaitingStage =
      request.stage === 'AWAITING_APPROVAL' ||
      request.sysRequestStage === 'AWAITING_APPROVAL';
    if (isDraftStage && !canViewDraftStageRows) {
      return false;
    }
    if (isAwaitingStage && !canViewAwaitingApprovalRows) {
      return false;
    }
    return true;
  };

  // Filter and sort requests (server handles role-based filtering for paginated server rows; client still gates merged offline rows)
  const filteredRequests = usePagination
    ? [
        ...offlineRequests.filter(request => canViewRequest(request)),
        ...paginatedServerRequests,
      ]
    : (() => {
        return (actualRequests || [])
          .filter(request => {
            if (!canViewRequest(request)) {
              return false;
            }

            // Search text filter
            if (debouncedSearchText) {
              const searchLower = debouncedSearchText.toLowerCase();
              const matchesSearch =
                (request.id || '').toLowerCase().includes(searchLower) ||
                (request.customerName || '')
                  .toLowerCase()
                  .includes(searchLower) ||
                (request.requestorName || '')
                  .toLowerCase()
                  .includes(searchLower) ||
                (request.region || '').toLowerCase().includes(searchLower) ||
                (request.stage || '').toLowerCase().includes(searchLower) ||
                (request.requestorEmail || '')
                  .toLowerCase()
                  .includes(searchLower);
              if (!matchesSearch) return false;
            }

            // Additional filters
            if (filterOptions.region && request.region !== filterOptions.region)
              return false;
            if (
              filterOptions.stage &&
              request.sysRequestStage !== filterOptions.stage
            )
              return false;
            if (filterOptions.modifiedOn) {
              const requestInstant = dateFormatting.parseInstant(
                request.sysModifiedOn || '',
              );
              const filterInstant = dateFormatting.parseInstant(
                filterOptions.modifiedOn,
              );
              if (requestInstant && filterInstant) {
                const requestDay = dateFormatting.calendarDay(requestInstant);
                const filterDay = dateFormatting.calendarDay(filterInstant);
                if (
                  requestDay.year !== filterDay.year ||
                  requestDay.month !== filterDay.month ||
                  requestDay.day !== filterDay.day
                ) {
                  return false;
                }
              } else if (
                (request.sysModifiedOn || '').slice(0, 10) !==
                filterOptions.modifiedOn.slice(0, 10)
              ) {
                return false;
              }
            }

            /**
             * ⚠️ `readStatus` IS NOT APPLIED HERE, AND MUST NOT BE. This is the
             * offline / non-paginated path, and rows served from the device
             * cache have had `hasUnseenChanges` stripped (it is per viewer), so
             * every one of them would look "read". Filtering on that would hand
             * back a confident, wrong answer; leaving the list unfiltered is
             * visibly "the filter did nothing", which is the honest failure.
             */
            return true;
          })
          .sort((a, b) => {
            const field = sortOptions.field as keyof Request;
            let aValue: any;
            let bValue: any;

            // The offline / non-paginated list has no server to defer to, so
            // Modified On is ordered here by the SAME value the column renders
            // (`modifiedOn`, falling back to `sysModifiedOn`), mirroring the
            // server's COALESCE. Without this branch it would fall through to
            // `a[field]` and read `sysModifiedOn` alone, which is empty on rows
            // whose date comes from `updated_at` — those would sort as 0 and
            // sink to the bottom of an offline list for no visible reason.
            //
            // ⚠️ NO TIE-BREAK IS POSSIBLE HERE: both values are dates with no
            // time, and the precise `updated_at` never reaches the client. Rows
            // modified on the same day therefore keep the cache's order offline,
            // where online they would be ordered by the real timestamp.
            if (sortOptions.field === DEFAULT_SORT.field) {
              const lastChangeOf = (request: Request) =>
                new Date(
                  request.modifiedOn || request.sysModifiedOn || 0,
                ).getTime() || 0;
              const aLast = lastChangeOf(a);
              const bLast = lastChangeOf(b);
              return sortOptions.direction === 'asc'
                ? aLast - bLast
                : bLast - aLast;
            }

            // Handle field mapping for client-side sorting
            if (field === 'dateCreated') {
              aValue = a.dateCreated;
              bValue = b.dateCreated;
            } else if (field === 'sysModifiedOn') {
              aValue = a.sysModifiedOn;
              bValue = b.sysModifiedOn;
            } else if (field === 'stage') {
              aValue = a.sysRequestStage;
              bValue = b.sysRequestStage;
            } else {
              // For other fields, use the field directly
              aValue = a[field];
              bValue = b[field];
            }

            // Handle date fields
            if (field === 'dateCreated' || field === 'sysModifiedOn') {
              const aDate = new Date(aValue as string);
              const bDate = new Date(bValue as string);

              // Handle invalid dates
              const aTime = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
              const bTime = isNaN(bDate.getTime()) ? 0 : bDate.getTime();

              return sortOptions.direction === 'asc'
                ? aTime - bTime
                : bTime - aTime;
            }

            // Handle string fields
            const aStr = ((aValue as string) || '').toLowerCase();
            const bStr = ((bValue as string) || '').toLowerCase();

            if (sortOptions.direction === 'asc') {
              return aStr.localeCompare(bStr);
            } else {
              return bStr.localeCompare(aStr);
            }
          });
      })();

  // Positioning functions for dropdowns
  const updateFilterDropdownPosition = () => {
    if (Platform.OS === 'web' && filterButtonRef.current) {
      const element = filterButtonRef.current as any;
      const rect = element.getBoundingClientRect();
      const dropdownWidth = Math.max(rect.width, 280);

      // Check if dropdown would overflow viewport on the right
      const spaceOnRight = window.innerWidth - rect.left;
      const shouldAlignRight = spaceOnRight < dropdownWidth;

      setFilterDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: shouldAlignRight
          ? rect.right + window.scrollX - dropdownWidth
          : rect.left + window.scrollX,
        width: dropdownWidth,
        // Viewport coordinates on purpose: `top` is a PAGE coordinate for the
        // absolutely-positioned panel, but what limits its height is where the
        // button sits on SCREEN.
        maxHeight: dropdownPanelMaxHeight(rect.bottom, window.innerHeight),
      });
    }
  };

  const updateSortDropdownPosition = () => {
    if (Platform.OS === 'web' && sortButtonRef.current) {
      const element = sortButtonRef.current as any;
      const rect = element.getBoundingClientRect();
      const dropdownWidth = Math.max(rect.width, 280);

      // Check if dropdown would overflow viewport on the right
      const spaceOnRight = window.innerWidth - rect.left;
      const shouldAlignRight = spaceOnRight < dropdownWidth;

      setSortDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: shouldAlignRight
          ? rect.right + window.scrollX - dropdownWidth
          : rect.left + window.scrollX,
        width: dropdownWidth,
        maxHeight: dropdownPanelMaxHeight(rect.bottom, window.innerHeight),
      });
    }
  };

  // Close dropdowns when clicking outside (web only)
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      (showFilterDropdown || showSortDropdown) &&
      typeof document !== 'undefined'
    ) {
      const handleClickOutside = (event: any) => {
        const filterPortal = document.querySelector(
          '[data-filter-dropdown-portal="true"]',
        );
        const sortPortal = document.querySelector(
          '[data-sort-dropdown-portal="true"]',
        );

        if (
          filterButtonRef.current &&
          !filterButtonRef.current.contains?.(event.target) &&
          (!filterPortal || !filterPortal.contains(event.target))
        ) {
          setShowFilterDropdown(false);
        }

        if (
          sortButtonRef.current &&
          !sortButtonRef.current.contains?.(event.target) &&
          (!sortPortal || !sortPortal.contains(event.target))
        ) {
          setShowSortDropdown(false);
        }

        // Check for page size dropdown click outside
        const pageSizeDropdown = document.querySelector('.page-size-dropdown');
        const pageSizeButton = document.querySelector('.page-size-button');
        if (
          showPageSizeDropdown &&
          pageSizeButton &&
          !pageSizeButton.contains(event.target) &&
          pageSizeDropdown &&
          !pageSizeDropdown.contains(event.target)
        ) {
          setShowPageSizeDropdown(false);
        }
      };

      const handleScroll = () => {
        if (showFilterDropdown) updateFilterDropdownPosition();
        if (showSortDropdown) updateSortDropdownPosition();
        // Page size dropdown doesn't need repositioning
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [showFilterDropdown, showSortDropdown, showPageSizeDropdown]);

  // Get unique values for filter options
  const getUniqueValues = (field: keyof Request) => {
    // Use actualRequests to get values from the current data set
    let values: any[] = [];

    if (field === 'stage') {
      values = actualRequests.map(req => req.sysRequestStage).filter(Boolean);
    } else if (field === 'modifiedOn') {
      values = actualRequests.map(req => req.sysModifiedOn).filter(Boolean);
    } else {
      values = actualRequests.map(req => req[field]).filter(Boolean);
    }

    const uniqueValues = Array.from(new Set(values)).sort();
    return uniqueValues;
  };

  const handleFilterChange = (
    field: keyof typeof filterOptions,
    value: string,
  ) => {
    setFilterOptions(prev => ({
      ...prev,
      [field]: value,
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
    // Close the dropdown after selection
    setShowFilterDropdown(false);
  };

  const handleSortChange = (field: string) => {
    setSortOptions(prev => {
      // If clicking the same field, toggle direction
      if (prev.field === field) {
        const newDirection = prev.direction === 'desc' ? 'asc' : 'desc';
        return {
          field,
          direction: newDirection,
        };
      }
      // If clicking a different field, start with 'asc' (A-Z)
      return {
        field,
        direction: 'asc',
      };
    });
    setShowSortDropdown(false);
  };

  const clearFilters = () => {
    setFilterOptions(defaultFilterOptions());
    setSearchText('');
    setShowFilterDropdown(false);
  };

  const clearSort = () => {
    setSortOptions({ ...DEFAULT_SORT });
    setShowSortDropdown(false);
  };

  const clearAll = () => {
    setFilterOptions(defaultFilterOptions());
    setSearchText('');
    setSortOptions({ ...DEFAULT_SORT });
    setShowFilterDropdown(false);
    setShowSortDropdown(false);
    setShowPageSizeDropdown(false);
  };

  const getSortDirectionLabel = (field: string, direction: 'asc' | 'desc') => {
    // For date fields, use more appropriate labels
    if (field === 'dateCreated' || field === 'sysModifiedOn') {
      return direction === 'asc' ? 'Oldest First' : 'Newest First';
    }
    // For string fields, use A-Z / Z-A
    return direction === 'asc' ? 'A-Z' : 'Z-A';
  };

  const handleRefresh = () => {
    actualRefetch();
  };

  const handleRequestPress = (requestId: string) => {
    navigate(`/request-detail?id=${requestId}`);
  };

  const handlePrivacyPolicyPress = async () => {
    const url =
      'https://www.vertiv.com/en-us/global-data-privacy-and-cookies-policy/';
    await openExternalLink(url);
  };

  const handleSupportLinkPress = async () => {
    const url = 'https://www.vertiv.com/en-us/support/';
    await openExternalLink(url);
  };

  // Ticket #808: Redesigned column layout
  // Request ID is now subtle text in row, not a column
  // Order: Customer Name | Project Name | Region | Created Date | Modified On | Modified By | Requestor Name | Stage | Actions
  const renderTableHeader = () => (
    <ListColumnHeader
      testID="request-table-header-cells"
      columnGap={headerColumnGap}
    >
      <ListColumnLabel
        testID="header-customer-name"
        style={styles.nameColumn}
      >
        Customer Name
      </ListColumnLabel>
      <ListColumnLabel testID="header-project-name" style={styles.nameColumn}>
        Project Name
      </ListColumnLabel>
      <ListColumnLabel testID="header-region" style={styles.regionColumn}>
        Region
      </ListColumnLabel>
      {showsCreatedAndModifiedBy && (
        <ListColumnLabel testID="header-created-date" style={styles.dateColumn}>
          Created Date
        </ListColumnLabel>
      )}
      <ListColumnLabel testID="header-modified-on" style={styles.dateColumn}>
        Modified On
      </ListColumnLabel>
      {showsCreatedAndModifiedBy && (
        <ListColumnLabel
          testID="header-modified-by"
          style={[styles.nameColumn, styles.modifiedByColumn]}
        >
          Modified By
        </ListColumnLabel>
      )}
      {showsRequestorColumn && (
        <ListColumnLabel
          testID="header-requestor-name"
          style={styles.nameColumn}
        >
          Requestor Name
        </ListColumnLabel>
      )}
      <ListColumnLabel
        testID="header-stage"
        style={[styles.stageColumn, styles.rightAlignHeader]}
      >
        Stage
      </ListColumnLabel>
      <View style={[styles.headerSlot, styles.actionsColumn]} />
    </ListColumnHeader>
  );

  /**
   * The row's discussion marker was pressed (owner change 2026-09-11): open
   * the request WITH the panel showing. The row's own press must not also
   * fire — on web both would, and the row's arrives with the panel closed.
   */
  const handleOpenDiscussion = (item: Request, event: any) => {
    event?.stopPropagation?.();
    navigate(requestDetailPathWithDiscussion(item.id));
  };

  const handleCopyRequest = async (item: Request, event: any) => {
    // Prevent the row click event from firing
    event.stopPropagation();

    try {
      const success = await copyRequestLinkToClipboard(item.id);
      if (success) {
        showToast.success('Request link copied to clipboard');
      } else {
        showToast.error('Failed to copy request link');
      }
    } catch (copyError) {
      console.error('Error copying request link:', copyError);
      showToast.error('Failed to copy request link');
    }
  };

  const handleEditRequest = async (item: Request, event: any) => {
    // Prevent the row click event from firing
    event.stopPropagation();

    // Invalidate the specific request query to ensure fresh data on detail page
    // This fixes the issue where stage shows as "PROCESSING" after transcription completes
    await queryClient.invalidateQueries({
      queryKey: offlineRequestsKeys.detail(item.id),
    });

    // Navigate to the request detail page which allows editing
    navigate(`/request-detail?id=${item.id}`);
  };

  // Ticket #808: Redesigned table row
  // Request ID is now subtle text positioned at top-left of row (not a column)
  // Column order: Customer Name | Project Name | Region | Created Date | Modified On | Modified By | Requestor Name | Stage | Actions
  const renderTableRow = (item: Request, index: number) => {
    const uniqueKey = item.id || `request-${index}`;
    const customerLabel = (item.customerName ?? '').trim();
    const projectLabel = (item.oppProjectName ?? '').trim();
    // #1575 FR-5. `=== true`, so an absent flag (degraded stamp, older server,
    // cached row) reads as seen and the row is never emphasised on a guess.
    const hasUnseenChanges = item.hasUnseenChanges === true;
    // `hasUnseenChanges === true` is the whole decision, for the weight and for
    // the ground alike (see the style docstring). Absent therefore draws as
    // READ, which is the contract #1069 set and this feature copied: a degraded
    // stamp, an older server, a stripped cache row or an offline `o-` draft
    // must never assert a change the page could not actually confirm.
    const unseenTextStyle = hasUnseenChanges
      ? styles.unreadRowText
      : styles.readRowText;
    const unseenSurfaceStyle = hasUnseenChanges
      ? styles.unreadRowSurface
      : styles.readRowSurface;

    return (
      <TouchableOpacity
        key={uniqueKey}
        testID={`request-row-${item.id}`}
        style={[
          styles.tableRow,
          styles.tableRowWithSubtleId,
          index % 2 === 0 && styles.evenRow,
          // Last, so the unread/read ground wins over the zebra stripe.
          unseenSurfaceStyle,
        ]}
        onPress={() => handleRequestPress(item.id)}
        activeOpacity={0.7}
        // Weight is not announced, so the row says the unread state in words.
        accessibilityLabel={getRequestRowAccessibilityLabel(item)}
      >
        {/* Subtle Request ID - positioned at top-left of row */}
        <View style={styles.subtleRequestIdContainer}>
          <Text style={[styles.subtleRequestId, unseenTextStyle]}>
            {item.id}
          </Text>
          <RequestFailedIndicator
            requestId={item.id}
            stage={item.stage || item.sysRequestStage}
            size="small"
            enabled={item.stage === 'DRAFT' || item.sysRequestStage === 'DRAFT'}
          />
        </View>

        <View
          style={styles.tableRowInner}
          testID={`request-row-${item.id}-cells`}
        >
          {/* Customer name — always shown on web (#1104) */}
          <TouchableOpacity
            testID={`request-row-${item.id}-customer`}
            style={[styles.cell, styles.nameColumn, styles.clickableCell]}
            onPress={() => handleRequestPress(item.id)}
          >
            <Text
              style={[styles.cellText, unseenTextStyle]}
              numberOfLines={1}
              ellipsizeMode="tail"
              {...(isWeb ? { title: customerLabel } : {})}
            >
              {customerLabel}
            </Text>
          </TouchableOpacity>

          {/* Project name — separate column on web (#1104) */}
          <TouchableOpacity
            testID={`request-row-${item.id}-project`}
            style={[styles.cell, styles.nameColumn, styles.clickableCell]}
            onPress={() => handleRequestPress(item.id)}
          >
            <Text
              style={[styles.cellText, unseenTextStyle]}
              numberOfLines={1}
              ellipsizeMode="tail"
              {...(isWeb ? { title: projectLabel } : {})}
            >
              {projectLabel}
            </Text>
          </TouchableOpacity>

          {/* Region */}
          <View
            style={[styles.cell, styles.regionColumn, styles.clickableCell]}
          >
            <Text style={[styles.cellText, unseenTextStyle]}>
              {item.region?.toUpperCase()}
            </Text>
          </View>

          {/* Created Date (wide tier only) */}
          {showsCreatedAndModifiedBy && (
            <View
              style={[styles.cell, styles.dateColumn, styles.clickableCell]}
            >
              <Text style={[styles.cellText, unseenTextStyle]}>
                {formatDate(item.dateCreated || '')}
              </Text>
            </View>
          )}

          {/* Modified On */}
          <View style={[styles.cell, styles.dateColumn, styles.clickableCell]}>
            <Text style={[styles.cellText, unseenTextStyle]}>
              {formatDate(item.modifiedOn || item.sysModifiedOn || '')}
            </Text>
          </View>

          {/* Modified By — single-line ellipsis; gutters from tableRowInner (wide tier only) */}
          {showsCreatedAndModifiedBy && (
            <View
              style={[
                styles.cell,
                styles.nameColumn,
                styles.modifiedByColumn,
                styles.clickableCell,
              ]}
            >
              <Text
                {...(isWeb
                  ? { title: item.modifiedBy || item.sysModifiedBy || '' }
                  : {})}
                style={[
                  styles.modifiedByDisplayText,
                  styles.modifiedByTableCellColor,
                  unseenTextStyle,
                ]}
                numberOfLines={1}
              >
                {item.modifiedBy || item.sysModifiedBy || ''}
              </Text>
            </View>
          )}

          {/* Requestor Name (not on the narrow tier) */}
          {showsRequestorColumn && (
            <View
              testID={`request-row-${item.id}-requestor`}
              style={[styles.cell, styles.nameColumn, styles.clickableCell]}
            >
              <Text
                style={[styles.cellText, unseenTextStyle]}
                numberOfLines={1}
                ellipsizeMode="tail"
                {...(isWeb ? { title: item.requestorName || '' } : {})}
              >
                {item.requestorName}
              </Text>
            </View>
          )}

          {/* Stage */}
          <View
            style={[
              styles.cell,
              styles.stageColumn,
              styles.clickableCell,
              styles.rightAlignCell,
            ]}
          >
            <View style={styles.stageBadgeWrapper}>
              {(item.stage === 'PROCESSING' ||
                item.sysRequestStage === 'PROCESSING') && (
                <ProcessingBadge size="small" />
              )}
              <RequestStageOrFailedBadge
                requestId={item.id}
                stage={item.stage || item.sysRequestStage}
                size="small"
                showInternalDetail={showInternalDetail}
                enabled={
                  item.stage === 'DRAFT' || item.sysRequestStage === 'DRAFT'
                }
              />
            </View>
          </View>

          {/* Actions */}
          <View
            style={[styles.cell, styles.actionsColumn, styles.rightAlignCell]}
          >
            <View style={styles.actionButtons}>
              {/* FR53 (Story 2.2): the marker; pressed, it opens the request with the discussion showing. */}
              <DiscussionRowIndicator
                hasDiscussion={item.hasDiscussion}
                hasUnread={item.hasUnread}
                hasUnreadMention={item.hasUnreadMention}
                onPress={event => handleOpenDiscussion(item, event)}
                style={styles.actionButton}
                testID={`request-row-${item.id}-discussion`}
              />
              <TouchableOpacity
                style={styles.actionButton}
                onPress={event => handleCopyRequest(item, event)}
              >
                <Copy size={16} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  /**
   * The card's accessibility (#1599 Story 3.2). The card is ONE accessible
   * element on iOS and Android, so the discussion marker's label and Copy's
   * press inside it are unreachable to VoiceOver and TalkBack. On native the
   * card's label therefore appends the marker's spoken state (the SAME
   * builder the marker uses, image form, lower-cased to sit mid-sentence),
   * and both row actions become the card's own `accessibilityActions`,
   * running the same handlers as the taps. Web keeps exactly today's label
   * and no actions: there the marker and Copy are reached directly.
   * `Platform.OS` is read here, at render, never at module scope.
   */
  const cardAccessibility = (item: Request) => {
    const base = getRequestRowAccessibilityLabel(item);
    if (Platform.OS === 'web') return { accessibilityLabel: base };
    const hasDiscussion = item.hasDiscussion === true;
    const marker = discussionMarkerLabel(
      'image',
      item.hasUnread === true,
      item.hasUnreadMention === true,
    );
    const actions: AccessibilityActionInfo[] = [
      ...(hasDiscussion
        ? [{ name: CARD_ACTION_OPEN_DISCUSSION, label: 'Open discussion' }]
        : []),
      { name: CARD_ACTION_COPY, label: 'Copy request' },
    ];
    return {
      accessibilityLabel: hasDiscussion
        ? `${base}, ${marker.charAt(0).toLowerCase()}${marker.slice(1)}`
        : base,
      accessibilityActions: actions,
      onAccessibilityAction: (event: AccessibilityActionEvent) => {
        // The handlers stop the tap reaching the card; an action has no
        // propagation to stop, so it hands them a no-op stand-in.
        if (event.nativeEvent.actionName === CARD_ACTION_OPEN_DISCUSSION) {
          if (hasDiscussion) handleOpenDiscussion(item, NO_PROPAGATION);
        } else if (event.nativeEvent.actionName === CARD_ACTION_COPY) {
          void handleCopyRequest(item, NO_PROPAGATION);
        }
      },
    };
  };

  const renderCard = (item: Request, index: number) => {
    const listPrimaryLabel = getRequestListPrimaryLabel(item);
    const isOfflineRequest = item.id.startsWith('o-');
    // Generate a unique key that handles edge cases
    const uniqueKey = item.id || `request-${index}`;
    // #1575 FR-5, same `=== true` as the table row: absent renders as seen.
    // Offline `o-` rows never carry the flag, so they never emphasise either.
    const hasUnseenChanges = item.hasUnseenChanges === true;
    // Same rule as the table row; an offline `o-` draft carries no flag, so it
    // draws as read.
    const unseenTextStyle = hasUnseenChanges
      ? styles.unreadRowText
      : styles.readRowText;
    const cardA11y = cardAccessibility(item);

    return (
      <TouchableOpacity
        key={uniqueKey}
        style={[
          styles.card,
          hasUnseenChanges ? styles.unreadRowSurface : styles.readRowSurface,
          // An offline draft keeps its own ground: it has no server flag to
          // reflect, and its state is the louder of the two.
          isOfflineRequest && styles.offlineCard,
        ]}
        onPress={() => handleRequestPress(item.id)}
        activeOpacity={0.7}
        // Same builder as the table row, so the two platforms cannot drift;
        // on native it also carries the marker's words (#1599 Story 3.2).
        {...cardA11y}
      >
        {/* Ticket #808: Redesigned card header */}
        {/* Request ID is now small and de-emphasized */}
        <View style={styles.cardTopRow}>
          <View style={styles.cardRequestIdContainer}>
            <Text style={styles.cardRequestIdSubtle}>{item.id}</Text>
            <RequestFailedIndicator
              requestId={item.id}
              stage={item.stage || item.sysRequestStage}
              size="small"
              enabled={
                item.stage === 'DRAFT' || item.sysRequestStage === 'DRAFT'
              }
            />
          </View>
          <View style={styles.cardTopActions}>
            {isOfflineRequest && (
              <View style={styles.offlineBadge}>
                <Text style={styles.offlineBadgeText}>OFFLINE</Text>
              </View>
            )}
            {(item.stage === 'PROCESSING' ||
              item.sysRequestStage === 'PROCESSING') && (
              <ProcessingBadge size="small" />
            )}
            <RequestStageOrFailedBadge
              requestId={item.id}
              stage={item.stage || item.sysRequestStage}
              size="small"
              showInternalDetail={showInternalDetail}
              enabled={
                item.stage === 'DRAFT' || item.sysRequestStage === 'DRAFT'
              }
            />
            {/* FR53 (Story 2.2): the marker; pressed, it opens the request with the discussion showing. */}
            <DiscussionRowIndicator
              hasDiscussion={item.hasDiscussion}
              hasUnread={item.hasUnread}
              hasUnreadMention={item.hasUnreadMention}
              onPress={event => handleOpenDiscussion(item, event)}
              style={styles.cardActionIcon}
              testID={`request-card-${item.id}-discussion`}
            />
            <TouchableOpacity
              style={styles.cardActionIcon}
              onPress={event => handleCopyRequest(item, event)}
            >
              <Copy size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Project name when populated, else customer (#1104) */}
        <Text
          testID={`request-card-${item.id}-list-primary`}
          style={[styles.cardCustomerName, unseenTextStyle]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {listPrimaryLabel}
        </Text>

        {/* Details Grid - Ticket #808: Removed Modified By, changed Modified to Created */}
        <View style={styles.cardContent}>
          <View style={styles.cardInfoGrid}>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>REQUESTOR</Text>
              <Text
                style={[styles.cardInfoValue, unseenTextStyle]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {item.requestorName}
              </Text>
            </View>

            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>REGION</Text>
              <Text style={[styles.cardInfoValue, unseenTextStyle]}>
                {item.region?.toUpperCase()}
              </Text>
            </View>
          </View>

          <View style={styles.cardInfoGrid}>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>CREATED</Text>
              <Text style={[styles.cardInfoValue, unseenTextStyle]}>
                {formatDate(item.dateCreated || '')}
              </Text>
            </View>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>MODIFIED ON</Text>
              <Text style={[styles.cardInfoValue, unseenTextStyle]}>
                {formatDate(item.modifiedOn || item.sysModifiedOn || '')}
              </Text>
            </View>
          </View>

          <View style={styles.cardInfoGrid}>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>MODIFIED BY</Text>
              <Text
                {...(isWeb
                  ? { title: item.modifiedBy || item.sysModifiedBy || '' }
                  : {})}
                style={[
                  styles.cardInfoValue,
                  styles.modifiedByDisplayText,
                  unseenTextStyle,
                ]}
                numberOfLines={1}
              >
                {item.modifiedBy || item.sysModifiedBy || ''}
              </Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderControls = () => (
    <View style={styles.controlsContainer}>
      {/* Search Bar - Full width on mobile/small screens */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputWrapper}>
          <Search size={16} color="#666" style={styles.searchIcon} />
          <TextInput
            testID="search-input"
            style={styles.searchInput}
            placeholder="Search requests..."
            value={searchText}
            onChangeText={setSearchText}
            placeholderTextColor="#999"
            returnKeyType="search"
            inputAccessoryViewID={
              Platform.OS === 'ios' ? ACCESSORY_VIEW_ID : undefined
            }
          />
        </View>
      </View>

      {/* Control Buttons - Below search on mobile/small screens */}
      <View style={styles.controlButtons}>
        <View style={styles.dropdownWrapper}>
          <TouchableOpacity
            testID="filter-button"
            ref={filterButtonRef}
            style={[
              styles.filterButton,
              (showFilterDropdown || filtersAreActive) &&
                styles.activeFilterButton,
            ]}
            onPress={() => {
              if (!showFilterDropdown) {
                updateFilterDropdownPosition();
                setShowSortDropdown(false); // Close sort when opening filter
              }
              setShowFilterDropdown(!showFilterDropdown);
            }}
          >
            <Filter
              size={14}
              color={
                showFilterDropdown || filtersAreActive ? '#ff6b35' : '#666'
              }
            />
            <Text
              style={[
                styles.buttonText,
                (showFilterDropdown || filtersAreActive) &&
                  styles.activeFilterText,
              ]}
            >
              Filter
              {filtersAreActive && ' (Active)'}
            </Text>
          </TouchableOpacity>

          {/* Filter Dropdown - Native Mobile (Modal) */}
          {Platform.OS !== 'web' && (
            <Modal
              visible={showFilterDropdown}
              transparent={true}
              animationType="fade"
              onRequestClose={() => setShowFilterDropdown(false)}
            >
              <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setShowFilterDropdown(false)}
              >
                <View
                  style={styles.modalContent}
                  onStartShouldSetResponder={() => true}
                >
                  <View style={styles.dropdown}>
                    <View style={styles.dropdownHeader}>
                      <Text style={styles.dropdownTitle}>Filter Options</Text>
                      <TouchableOpacity onPress={clearAll}>
                        <Text style={styles.clearFiltersText}>Clear All</Text>
                      </TouchableOpacity>
                    </View>

                    <ScrollView
                      style={styles.dropdownContent}
                      /**
                       * #1640: the indicator stays ON here. This list is five
                       * sections long and does not fit the modal, and a
                       * scroller with no indicator reads as a SHORT LIST —
                       * which is exactly how the web panel hid "Without
                       * discussion" until it was sized to its content.
                       */
                      showsVerticalScrollIndicator={true}
                    >
                      {/* #1575: read state is decided per viewer on the server,
                          so this is a normal filter on the wire like the rest. */}
                      <View style={styles.dropdownSection}>
                        <View style={styles.dropdownSectionHeader}>
                          <Text style={styles.dropdownSectionLabel}>
                            Read Status
                          </Text>
                          {filterOptions.readStatus ? (
                            <TouchableOpacity
                              onPress={() =>
                                handleFilterChange('readStatus', '')
                              }
                            >
                              <Text style={styles.clearSectionText}>Clear</Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <View style={styles.dropdownOptions}>
                          {[
                            { value: '', label: 'All' },
                            { value: 'unread', label: 'Unread' },
                            { value: 'read', label: 'Read' },
                          ].map(option => (
                            <TouchableOpacity
                              key={`readStatus-${option.value || 'all'}`}
                              testID={`filter-read-status-${
                                option.value || 'all'
                              }`}
                              style={[
                                styles.dropdownOption,
                                filterOptions.readStatus === option.value &&
                                  styles.dropdownOptionActive,
                              ]}
                              onPress={() =>
                                handleFilterChange('readStatus', option.value)
                              }
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  filterOptions.readStatus === option.value &&
                                    styles.dropdownOptionTextActive,
                                ]}
                              >
                                {option.label}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>

                      {/* #1640: "has a discussion" is decided per viewer on
                          the server too -- a conversation in an audience this
                          person may not read does not count -- so like Read
                          Status it travels as a normal filter on the wire. */}
                      <View style={styles.dropdownSection}>
                        <View style={styles.dropdownSectionHeader}>
                          <Text style={styles.dropdownSectionLabel}>
                            Discussion
                          </Text>
                          {filterOptions.discussionStatus ? (
                            <TouchableOpacity
                              onPress={() =>
                                handleFilterChange('discussionStatus', '')
                              }
                            >
                              <Text style={styles.clearSectionText}>Clear</Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <View style={styles.dropdownOptions}>
                          {DISCUSSION_FILTER_OPTIONS.map(option => (
                            <TouchableOpacity
                              key={`discussionStatus-${option.value || 'all'}`}
                              testID={`filter-discussion-status-${
                                option.value || 'all'
                              }`}
                              style={[
                                styles.dropdownOption,
                                filterOptions.discussionStatus ===
                                  option.value && styles.dropdownOptionActive,
                              ]}
                              onPress={() =>
                                handleFilterChange(
                                  'discussionStatus',
                                  option.value,
                                )
                              }
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  filterOptions.discussionStatus ===
                                    option.value &&
                                    styles.dropdownOptionTextActive,
                                ]}
                              >
                                {option.label}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>

                      <View style={styles.dropdownSection}>
                        <View style={styles.dropdownSectionHeader}>
                          <Text style={styles.dropdownSectionLabel}>
                            Region
                          </Text>
                          {filterOptions.region && (
                            <TouchableOpacity
                              onPress={() => handleFilterChange('region', '')}
                            >
                              <Text style={styles.clearSectionText}>Clear</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        <View style={styles.dropdownOptions}>
                          <TouchableOpacity
                            style={[
                              styles.dropdownOption,
                              filterOptions.region === '' &&
                                styles.dropdownOptionActive,
                            ]}
                            onPress={() => handleFilterChange('region', '')}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                filterOptions.region === '' &&
                                  styles.dropdownOptionTextActive,
                              ]}
                            >
                              All
                            </Text>
                          </TouchableOpacity>
                          {getUniqueValues('region').map(region => (
                            <TouchableOpacity
                              key={`region-${String(region)}`}
                              style={[
                                styles.dropdownOption,
                                filterOptions.region === region &&
                                  styles.dropdownOptionActive,
                              ]}
                              onPress={() =>
                                handleFilterChange('region', region as string)
                              }
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  filterOptions.region === region &&
                                    styles.dropdownOptionTextActive,
                                ]}
                              >
                                {String(region).toUpperCase()}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>

                      <View style={styles.dropdownSection}>
                        <View style={styles.dropdownSectionHeader}>
                          <Text style={styles.dropdownSectionLabel}>Stage</Text>
                          {filterOptions.stage && (
                            <TouchableOpacity
                              onPress={() => handleFilterChange('stage', '')}
                            >
                              <Text style={styles.clearSectionText}>Clear</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        <View style={styles.dropdownOptions}>
                          <TouchableOpacity
                            style={[
                              styles.dropdownOption,
                              filterOptions.stage === '' &&
                                styles.dropdownOptionActive,
                            ]}
                            onPress={() => handleFilterChange('stage', '')}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                filterOptions.stage === '' &&
                                  styles.dropdownOptionTextActive,
                              ]}
                            >
                              All
                            </Text>
                          </TouchableOpacity>
                          {stageFilterOptions
                            .filter(stage => {
                              if (
                                stage.value === 'DRAFT' ||
                                stage.value === 'AWAITING_APPROVAL'
                              ) {
                                const canViewDraftLikeStages =
                                  user &&
                                  (roleService.isRequestor(user) ||
                                    roleService.isExternalRequestor(user) ||
                                    roleService.isITAdministrator(user));
                                return canViewDraftLikeStages;
                              }
                              return true;
                            })
                            .map(stage => (
                              <TouchableOpacity
                                key={`stage-${stage.value}`}
                                style={[
                                  styles.dropdownOption,
                                  filterOptions.stage === stage.value &&
                                    styles.dropdownOptionActive,
                                ]}
                                onPress={() =>
                                  handleFilterChange('stage', stage.value)
                                }
                              >
                                <Text
                                  style={[
                                    styles.dropdownOptionText,
                                    filterOptions.stage === stage.value &&
                                      styles.dropdownOptionTextActive,
                                  ]}
                                >
                                  {stage.label}
                                </Text>
                              </TouchableOpacity>
                            ))}
                        </View>
                      </View>

                      <View style={styles.dropdownSection}>
                        <View style={styles.dropdownSectionHeader}>
                          <Text style={styles.dropdownSectionLabel}>
                            Modified On
                          </Text>
                          {filterOptions.modifiedOn && (
                            <TouchableOpacity
                              onPress={() =>
                                handleFilterChange('modifiedOn', '')
                              }
                            >
                              <Text style={styles.clearSectionText}>Clear</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        <View style={styles.dropdownOptions}>
                          <TouchableOpacity
                            style={[
                              styles.dropdownOption,
                              filterOptions.modifiedOn === '' &&
                                styles.dropdownOptionActive,
                            ]}
                            onPress={() => handleFilterChange('modifiedOn', '')}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                filterOptions.modifiedOn === '' &&
                                  styles.dropdownOptionTextActive,
                              ]}
                            >
                              All
                            </Text>
                          </TouchableOpacity>
                          {[
                            { value: '7', label: 'Last 7 Days' },
                            { value: '15', label: 'Last 15 Days' },
                            { value: '30', label: 'Last 30 Days' },
                          ].map(option => (
                            <TouchableOpacity
                              key={`modifiedOn-${option.value}`}
                              style={[
                                styles.dropdownOption,
                                filterOptions.modifiedOn === option.value &&
                                  styles.dropdownOptionActive,
                              ]}
                              onPress={() =>
                                handleFilterChange('modifiedOn', option.value)
                              }
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  filterOptions.modifiedOn === option.value &&
                                    styles.dropdownOptionTextActive,
                                ]}
                              >
                                {option.label}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    </ScrollView>
                  </View>
                </View>
              </TouchableOpacity>
            </Modal>
          )}
        </View>

        <View style={styles.dropdownWrapper}>
          <TouchableOpacity
            testID="sort-button"
            ref={sortButtonRef}
            style={[
              styles.sortButton,
              (showSortDropdown || !sortIsDefault) && styles.activeSortButton,
            ]}
            onPress={() => {
              if (!showSortDropdown) {
                updateSortDropdownPosition();
                setShowFilterDropdown(false); // Close filter when opening sort
              }
              setShowSortDropdown(!showSortDropdown);
            }}
          >
            <ArrowUpDown
              size={14}
              color={showSortDropdown || !sortIsDefault ? '#ff6b35' : '#666'}
            />
            <Text
              style={[
                styles.buttonText,
                (showSortDropdown || !sortIsDefault) && styles.activeSortText,
              ]}
            >
              Sort
            </Text>
          </TouchableOpacity>

          {/* Sort Dropdown - Native Mobile (Modal) */}
          {Platform.OS !== 'web' && (
            <Modal
              visible={showSortDropdown}
              transparent={true}
              animationType="fade"
              onRequestClose={() => setShowSortDropdown(false)}
            >
              <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={() => setShowSortDropdown(false)}
              >
                <View
                  style={styles.modalContent}
                  onStartShouldSetResponder={() => true}
                >
                  <View style={styles.dropdown}>
                    <View style={styles.dropdownHeader}>
                      <Text style={styles.dropdownTitle}>Sort Options</Text>
                      <TouchableOpacity onPress={clearSort}>
                        <Text style={styles.clearFiltersText}>Reset</Text>
                      </TouchableOpacity>
                    </View>

                    <ScrollView
                      style={styles.dropdownContent}
                      /**
                       * #1640: on for the same reason as the filter modal's —
                       * a scroller with no indicator reads as a short list.
                       */
                      showsVerticalScrollIndicator={true}
                    >
                      {[
                        { field: 'dateCreated', label: 'Date Created' },
                        { field: 'sysModifiedOn', label: 'Modified On' },
                        { field: 'customerName', label: 'Customer Name' },
                        { field: 'id', label: 'Request ID' },
                        { field: 'requestorName', label: 'Requestor Name' },
                        { field: 'region', label: 'Region' },
                        { field: 'stage', label: 'Stage' },
                      ].map(option => (
                        <TouchableOpacity
                          key={option.field}
                          style={[
                            styles.dropdownOption,
                            sortOptions.field === option.field &&
                              styles.dropdownOptionActive,
                          ]}
                          onPress={() => handleSortChange(option.field)}
                        >
                          <Text
                            style={[
                              styles.dropdownOptionText,
                              sortOptions.field === option.field &&
                                styles.dropdownOptionTextActive,
                            ]}
                          >
                            {option.label}
                            {sortOptions.field === option.field && (
                              <Text style={styles.sortDirectionText}>
                                {' '}
                                (
                                {getSortDirectionLabel(
                                  option.field,
                                  sortOptions.direction,
                                )}
                                )
                              </Text>
                            )}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                </View>
              </TouchableOpacity>
            </Modal>
          )}
        </View>

        {/* Only show Reset button when filters or sort are active */}
        {(filtersAreActive || searchText || !sortIsDefault) && (
          <TouchableOpacity style={styles.resetButton} onPress={clearAll}>
            <RotateCcw size={14} color="#666" />
            <Text style={styles.buttonText}>Reset</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  const renderPagination = () => {
    if (usePagination && paginatedData) {
      const { pagination } = paginatedData;
      const startItem = (pagination.page - 1) * pagination.pageSize + 1;
      const endItem = Math.min(
        pagination.page * pagination.pageSize,
        pagination.total,
      );

      return (
        <View style={styles.paginationContainer}>
          <View testID="pagination-info-top" style={styles.paginationInfo}>
            <Text style={styles.paginationText}>
              {shouldShowTable
                ? `Showing ${startItem}-${endItem} of ${pagination.total} requests`
                : `${startItem}-${endItem} of ${pagination.total}`}
            </Text>
          </View>

          <View style={styles.paginationControls}>
            {shouldShowTable && (
              <Text style={styles.paginationLabel}>Rows per page:</Text>
            )}
            <View style={styles.pageSizeContainer}>
              <TouchableOpacity
                style={styles.pageSizeDropdown}
                onPress={() => setShowPageSizeDropdown(!showPageSizeDropdown)}
                {...(Platform.OS === 'web' && {
                  className: 'page-size-button',
                })}
              >
                <Text style={styles.pageSizeText}>{pageSize}</Text>
                <View
                  style={{
                    marginLeft: 6,
                    transform: [
                      { rotate: showPageSizeDropdown ? '180deg' : '0deg' },
                    ],
                  }}
                >
                  <ChevronDown size={10} color="#666" />
                </View>
              </TouchableOpacity>

              {showPageSizeDropdown && (
                <View
                  style={styles.pageSizeOptions}
                  {...(Platform.OS === 'web' && {
                    className: 'page-size-dropdown',
                  })}
                >
                  {REQUEST_LIST_PAGE_SIZES.map(size => (
                    <TouchableOpacity
                      key={size}
                      style={[
                        styles.pageSizeOption,
                        pageSize === size && styles.pageSizeOptionActive,
                      ]}
                      onPress={() => {
                        setPageSize(size);
                        setCurrentPage(1);
                        setShowPageSizeDropdown(false);
                      }}
                    >
                      <Text
                        style={[
                          styles.pageSizeOptionText,
                          pageSize === size && styles.pageSizeOptionTextActive,
                        ]}
                      >
                        {size}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            <TouchableOpacity
              style={[
                styles.pageButton,
                currentPage === 1 && styles.pageButtonDisabled,
              ]}
              onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              {shouldShowTable ? (
                <Text
                  style={[
                    styles.pageButtonText,
                    currentPage === 1 && styles.pageButtonTextDisabled,
                  ]}
                >
                  Previous
                </Text>
              ) : (
                <ChevronLeft
                  size={16}
                  color={currentPage === 1 ? '#999' : '#333'}
                />
              )}
            </TouchableOpacity>

            <Text style={styles.pageInfo}>
              {shouldShowTable
                ? `Page ${currentPage} of ${pagination.totalPages}`
                : `${currentPage}/${pagination.totalPages}`}
            </Text>

            <TouchableOpacity
              style={[
                styles.pageButton,
                currentPage === pagination.totalPages &&
                  styles.pageButtonDisabled,
              ]}
              onPress={() =>
                setCurrentPage(prev =>
                  Math.min(pagination.totalPages, prev + 1),
                )
              }
              disabled={currentPage === pagination.totalPages}
            >
              {shouldShowTable ? (
                <Text
                  style={[
                    styles.pageButtonText,
                    currentPage === pagination.totalPages &&
                      styles.pageButtonTextDisabled,
                  ]}
                >
                  Next
                </Text>
              ) : (
                <ChevronRight
                  size={16}
                  color={
                    currentPage === pagination.totalPages ? '#999' : '#333'
                  }
                />
              )}
            </TouchableOpacity>
          </View>
        </View>
      );
    } else {
      return (
        <View style={styles.paginationContainer}>
          <View testID="pagination-info-bottom" style={styles.paginationInfo}>
            <Text style={styles.paginationText}>
              {shouldShowTable
                ? `Showing ${filteredRequests?.length || 0} of ${
                    actualRequests?.length || 0
                  } requests${
                    filtersAreActive || searchText ? ' (filtered)' : ''
                  }`
                : `${filteredRequests?.length || 0} of ${
                    actualRequests?.length || 0
                  }`}
            </Text>
          </View>
        </View>
      );
    }
  };

  const renderLoadingState = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color="#007bff" />
      <Text style={styles.loadingText}>Loading requests...</Text>
    </View>
  );

  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <AlertCircle size={48} color="#dc3545" />
      <Text style={styles.errorTitle}>Unable to load requests</Text>
      <Text style={styles.errorMessage}>
        {error instanceof Error
          ? error.message
          : 'Please check your connection and try again.'}
      </Text>
      <TouchableOpacity style={styles.retryButton} onPress={handleRefresh}>
        <RefreshCw size={16} color="#fff" />
        <Text style={styles.retryButtonText}>Try Again</Text>
      </TouchableOpacity>
    </View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Text testID="empty-state-title" style={styles.emptyTitle}>
        No requests found
      </Text>
      <Text testID="empty-state-message" style={styles.emptyMessage}>
        {searchText
          ? 'Try adjusting your search criteria.'
          : filterOptions.stage === 'AWAITING_APPROVAL'
          ? 'No requests waiting for approval.'
          : 'No requests have been created yet.'}
      </Text>
    </View>
  );

  // Create dynamic styles inside component
  const styles = createStyles({
    shouldShowTable,
    tableColumnTier,
    isTablet,
    isLargeMobile,
    isMobileLandscape,
  });

  return (
    <View style={styles.container}>
      <Header navigate={navigate} currentPath={currentPath} />

      <SubHeader
        title=""
        navigate={navigate}
        showNewRequestButton={false}
        rightElement={
          <NewRequestSplitButton
            onNewRequest={() => navigate('/create-request?fresh=1')}
            onImportFile={handleImportFile}
            onImportAudio={handleImportAudio}
            onImportText={handleImportText}
            canShowTextIntake={draftButtonPermissions.canShowTextIntake}
            hasCreatePermission={
              roleService.canCreateRequest(user) && !isReadOnlyImpersonation
            }
            disabled={false}
          />
        }
      />

      <ScrollView
        style={styles.contentWrapper}
        contentContainerStyle={styles.contentWrapperContent}
      >
        {renderControls()}

        {isDataLoading ? (
          renderLoadingState()
        ) : shouldShowError ? (
          renderErrorState()
        ) : (filteredRequests?.length || 0) === 0 ? (
          renderEmptyState()
        ) : shouldShowTable ? (
          <View style={styles.tableContainer}>
            <View style={styles.tableWrapper}>
              {renderTableHeader()}
              {filteredRequests.map((item, index) =>
                renderTableRow(item, index),
              )}
            </View>
          </View>
        ) : (
          <View style={styles.cardsContainer}>
            <View style={styles.cardsGrid}>
              {filteredRequests.map((item, index) => renderCard(item, index))}
            </View>
          </View>
        )}

        {/* Inline Pagination and Footer with Safe Area */}
        {!isDataLoading &&
          !shouldShowError &&
          (filteredRequests?.length || 0) > 0 && (
            <SafeBottomArea testID="view-requests-safe-bottom">
              <View style={styles.paginationWrapper}>{renderPagination()}</View>
              <View
                style={styles.privacyFooterContainer}
                testID="privacy-footer"
              >
                <TouchableOpacity
                  testID="privacy-policy-link"
                  onPress={handlePrivacyPolicyPress}
                >
                  <Text style={styles.privacyLinkText}>
                    Data Privacy and Cookies Policy
                  </Text>
                </TouchableOpacity>
                <View style={styles.confidentialContainer}>
                  <Text style={styles.confidentialText}>
                    This application and its contents contain confidential and
                    proprietary information intended exclusively for authorized
                    users. Unauthorized access, use, disclosure, or distribution
                    of this information is strictly prohibited and may be
                    subject to legal action. By accessing this application, you
                    acknowledge that you are an authorized user. Any sharing,
                    copying, distribution, or disclosure of this information to
                    unauthorized third parties is expressly forbidden. If you
                    have received access to this application in error or have
                    any questions regarding authorization, please contact our
                    support team immediately at{' '}
                    <Text
                      style={styles.supportLinkText}
                      onPress={handleSupportLinkPress}
                    >
                      https://www.vertiv.com/en-us/support/
                    </Text>
                    . Meanwhile, please refrain from using this application
                    until you have received explicit approval from the
                    appropriate authority.
                  </Text>
                </View>
              </View>
            </SafeBottomArea>
          )}
      </ScrollView>

      {/* Unobtrusive Refresh Indicator */}
      <RefreshIndicator isVisible={actualIsFetching} />

      {/* Web Portal Dropdowns */}
      {Platform.OS === 'web' &&
        showFilterDropdown &&
        createPortal &&
        createPortal(
          <div
            data-filter-dropdown-portal="true"
            style={getDropdownPortalRootStyle(filterDropdownPosition)}
          >
            <div style={dropdownPortalHeaderRow}>
              <span style={dropdownPortalTitle}>Filter Options</span>
              <button onClick={clearAll} style={dropdownPortalHeaderButton}>
                Clear All
              </button>
            </div>

            <div style={dropdownPortalScrollArea}>
              {/* Read Status Filter (#1575) — the web portal's copy of the
                  section; the React Native branch above has its own. */}
              <div style={filterSectionPadding}>
                <div style={filterSectionHeaderRow}>
                  <div style={filterSectionLabel}>Read Status</div>
                  {filterOptions.readStatus && (
                    <button
                      onClick={() => handleFilterChange('readStatus', '')}
                      style={filterSectionClearButton}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div>
                  {[
                    { value: '', label: 'All' },
                    { value: 'unread', label: 'Unread' },
                    { value: 'read', label: 'Read' },
                  ].map(option => (
                    <div
                      key={option.value || 'all'}
                      data-testid={`filter-read-status-${
                        option.value || 'all'
                      }`}
                      style={getFilterOptionRowStyle(
                        filterOptions.readStatus === option.value,
                      )}
                      onClick={() =>
                        handleFilterChange('readStatus', option.value)
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Discussion Filter (#1640) -- the web portal's copy of the
                  section; the React Native branch above has its own. */}
              <div style={filterSectionPadding}>
                <div style={filterSectionHeaderRow}>
                  <div style={filterSectionLabel}>Discussion</div>
                  {filterOptions.discussionStatus && (
                    <button
                      onClick={() => handleFilterChange('discussionStatus', '')}
                      style={filterSectionClearButton}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div>
                  {DISCUSSION_FILTER_OPTIONS.map(option => (
                    <div
                      key={option.value || 'all'}
                      data-testid={`filter-discussion-status-${
                        option.value || 'all'
                      }`}
                      style={getFilterOptionRowStyle(
                        filterOptions.discussionStatus === option.value,
                      )}
                      onClick={() =>
                        handleFilterChange('discussionStatus', option.value)
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Region Filter */}
              <div style={filterSectionPadding}>
                <div style={filterSectionHeaderRow}>
                  <div style={filterSectionLabel}>Region</div>
                  {filterOptions.region && (
                    <button
                      onClick={() => handleFilterChange('region', '')}
                      style={filterSectionClearButton}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div>
                  <div
                    style={getFilterOptionRowStyle(filterOptions.region === '')}
                    onClick={() => handleFilterChange('region', '')}
                  >
                    All
                  </div>
                  {getUniqueValues('region').map((region: any) => (
                    <div
                      key={`region-${String(region)}`}
                      style={getFilterOptionRowStyle(
                        filterOptions.region === region,
                      )}
                      onClick={() =>
                        handleFilterChange('region', region as string)
                      }
                    >
                      {String(region).toUpperCase()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Stage Filter */}
              <div style={filterSectionPadding}>
                <div style={filterSectionHeaderRow}>
                  <div style={filterSectionLabel}>Stage</div>
                  {filterOptions.stage && (
                    <button
                      onClick={() => handleFilterChange('stage', '')}
                      style={filterSectionClearButton}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div>
                  <div
                    style={getFilterOptionRowStyle(filterOptions.stage === '')}
                    onClick={() => handleFilterChange('stage', '')}
                  >
                    All
                  </div>
                  {stageFilterOptions
                    .filter(stage => {
                      if (
                        stage.value === 'DRAFT' ||
                        stage.value === 'AWAITING_APPROVAL'
                      ) {
                        const canViewDraftLikeStages =
                          user &&
                          (roleService.isRequestor(user) ||
                            roleService.isExternalRequestor(user) ||
                            roleService.isITAdministrator(user));
                        return canViewDraftLikeStages;
                      }
                      return true;
                    })
                    .map(stage => (
                      <div
                        key={stage.value}
                        style={getFilterOptionRowStyle(
                          filterOptions.stage === stage.value,
                        )}
                        onClick={() => handleFilterChange('stage', stage.value)}
                      >
                        {stage.label}
                      </div>
                    ))}
                </div>
              </div>

              {/* Modified On Filter */}
              <div style={filterSectionPadding}>
                <div style={filterSectionHeaderRow}>
                  <div style={filterSectionLabel}>Modified On</div>
                  {filterOptions.modifiedOn && (
                    <button
                      onClick={() => handleFilterChange('modifiedOn', '')}
                      style={filterSectionClearButton}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div>
                  <div
                    style={getFilterOptionRowStyle(
                      filterOptions.modifiedOn === '',
                    )}
                    onClick={() => handleFilterChange('modifiedOn', '')}
                  >
                    All
                  </div>
                  {[
                    { value: '7', label: 'Last 7 Days' },
                    { value: '15', label: 'Last 15 Days' },
                    { value: '30', label: 'Last 30 Days' },
                  ].map(option => (
                    <div
                      key={option.value}
                      style={getFilterOptionRowStyle(
                        filterOptions.modifiedOn === option.value,
                      )}
                      onClick={() =>
                        handleFilterChange('modifiedOn', option.value)
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          typeof document !== 'undefined' ? document.body : (null as any),
        )}

      {Platform.OS === 'web' &&
        showSortDropdown &&
        createPortal &&
        createPortal(
          <div
            data-sort-dropdown-portal="true"
            style={getDropdownPortalRootStyle(sortDropdownPosition)}
          >
            <div style={dropdownPortalHeaderRow}>
              <span style={dropdownPortalTitle}>Sort Options</span>
              <button onClick={clearSort} style={dropdownPortalHeaderButton}>
                Reset
              </button>
            </div>

            <div style={dropdownPortalScrollArea}>
              {[
                { field: 'dateCreated', label: 'Date Created' },
                { field: 'sysModifiedOn', label: 'Modified On' },
                { field: 'customerName', label: 'Customer Name' },
                { field: 'id', label: 'Request ID' },
                { field: 'requestorName', label: 'Requestor Name' },
                { field: 'region', label: 'Region' },
                { field: 'stage', label: 'Stage' },
              ].map(option => (
                <div
                  key={option.field}
                  style={getSortOptionRowStyle(
                    sortOptions.field === option.field,
                  )}
                  onClick={() => handleSortChange(option.field)}
                >
                  {option.label}
                  {sortOptions.field === option.field && (
                    <span style={sortDirectionHint}>
                      {' '}
                      (
                      {getSortDirectionLabel(
                        option.field,
                        sortOptions.direction,
                      )}
                      )
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>,
          typeof document !== 'undefined' ? document.body : (null as any),
        )}

      {/* AWS Transcription Modals */}
      <ProcessingModal
        visible={processingModalVisible}
        onClose={() => setProcessingModalVisible(false)}
        requestId={processingRequestId}
        fileName={processingFileName}
        autoCloseOnComplete={processingModalAutoClose}
        onNavigateToRequest={handleNavigateToRequest}
      />

      <TextIntakeModal
        visible={textIntakeModalVisible}
        onClose={() => setTextIntakeModalVisible(false)}
        onSubmit={handleTextIntakeSubmit}
        isSubmitting={textIntakeSubmitting}
        submitLabel="Create Request"
      />

      {/* OCR Source Selection Modal (mobile only) */}
      <OCRSourceSelectionModal
        visible={ocrSourceModalVisible}
        onClose={() => setOcrSourceModalVisible(false)}
        onSelect={handleOCRSourceSelect}
      />

      {/* iOS Keyboard Dismiss Toolbar */}
      <KeyboardDismissToolbar />
    </View>
  );
};

const createStyles = ({
  shouldShowTable,
  tableColumnTier,
  isTablet,
  isLargeMobile,
  isMobileLandscape,
}: {
  shouldShowTable: boolean;
  tableColumnTier: TableColumnTier;
  isTablet: boolean;
  isLargeMobile: boolean;
  isMobileLandscape: boolean;
}) => {
  // Tighter gutters once columns start collapsing (Sally, 2026-09-11); the
  // wide tier keeps today's `tableColumnGap`. One value for header AND rows.
  // Only the web TABLE tightens; the card renderer never reads these styles,
  // and gating here keeps that true by construction rather than by luck.
  const tableColumnGap =
    shouldShowTable && tableColumnTier !== 'wide'
      ? spacing.sm
      : vrLayout.tableColumnGap;
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.surfaceHover,
    },
    contentWrapper: {
      flex: 1,
    },
    contentWrapperContent: {
      padding: 20,
      alignItems: 'center',
    },
    cardsContent: {
      alignItems: shouldShowTable ? 'center' : 'stretch',
      width: '100%',
    },
    controlsContainer: {
      // Use column layout on mobile, row on tablet/desktop
      flexDirection: shouldShowTable ? 'row' : 'column',
      justifyContent: shouldShowTable ? 'space-between' : 'flex-start',
      alignItems: shouldShowTable ? 'center' : 'stretch',
      marginBottom: 20,
      width: '100%',
      maxWidth: 1200,
      gap: shouldShowTable ? 0 : spacing.md,
    },
    searchContainer: {
      // Full width on mobile, max 400px on desktop
      flex: shouldShowTable ? 1 : undefined,
      maxWidth: shouldShowTable ? 400 : undefined,
      width: shouldShowTable ? undefined : '100%',
    },
    searchInputWrapper: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.backgroundPrimary,
      borderWidth: 1,
      borderColor: colors.borderMedium,
      borderRadius: isTablet ? 8 : 6,
      paddingHorizontal: isTablet ? 16 : 12,
      height: isTablet ? 44 : 40,
    },
    searchIcon: {
      marginRight: 8,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: colors.textPrimary,
    },
    controlButtons: {
      flexDirection: 'row',
      gap: isTablet ? spacing.lg : spacing.md,
      flexWrap: shouldShowTable ? 'nowrap' : 'wrap',
      width: shouldShowTable ? undefined : '100%',
      justifyContent: 'flex-end', // Always align to the right
    },
    dropdownWrapper: {
      position: 'relative',
      zIndex: 99999,
    },
    filterButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: isTablet ? 20 : spacing.lg,
      paddingVertical: isTablet ? spacing.md : spacing.sm,
      backgroundColor: colors.backgroundPrimary,
      borderWidth: 1,
      borderColor: colors.borderMedium,
      borderRadius: isTablet ? 8 : 6,
      gap: isTablet ? 8 : 6,
    },
    sortButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: isTablet ? 20 : spacing.lg,
      paddingVertical: isTablet ? spacing.md : spacing.sm,
      backgroundColor: colors.backgroundPrimary,
      borderWidth: 1,
      borderColor: colors.borderMedium,
      borderRadius: isTablet ? 8 : 6,
      gap: isTablet ? 8 : 6,
    },
    refreshButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: isTablet ? 20 : spacing.lg,
      paddingVertical: isTablet ? spacing.md : spacing.sm,
      backgroundColor: colors.backgroundPrimary,
      borderWidth: 1,
      borderColor: colors.borderMedium,
      borderRadius: isTablet ? 8 : 6,
      gap: isTablet ? 8 : 6,
    },
    resetButton: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: isTablet ? 20 : spacing.lg,
      paddingVertical: isTablet ? spacing.md : spacing.sm,
      backgroundColor: colors.backgroundPrimary,
      borderWidth: 1,
      borderColor: colors.borderMedium,
      borderRadius: isTablet ? 8 : 6,
      gap: isTablet ? 8 : 6,
    },
    buttonText: {
      fontSize: 14,
      color: colors.textPrimary,
      fontWeight: '500',
    },
    activeFilterButton: {
      borderColor: colors.primary,
      backgroundColor: colors.surfaceAccentMuted,
    },
    activeFilterText: {
      color: colors.primary,
    },
    activeSortButton: {
      borderColor: colors.primary,
      backgroundColor: colors.surfaceAccentMuted,
    },
    activeSortText: {
      color: colors.primary,
    },
    tableContainer: {
      width: '100%',
      maxWidth: 1200,
    },
    tableWrapper: {
      width: '100%',
    },
    headerSlot: {
      flexShrink: 0,
    },
    tableRow: {
      paddingVertical: vrLayout.tablePaddingVertical,
      paddingHorizontal: vrLayout.tablePaddingHorizontal,
      minHeight: 60,
      backgroundColor: colors.backgroundPrimary,
      marginBottom: 10,
      borderRadius: 4,
      shadowColor: colors.shadowBase,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 3,
      width: '100%',
      ...Platform.select({
        web: {
          cursor: 'pointer',
          ':hover': {
            backgroundColor: colors.surfaceHover,
          },
        },
        default: {},
      }),
    },
    tableRowInner: {
      flexDirection: 'row',
      alignItems: 'stretch',
      alignSelf: 'stretch',
      width: '100%',
      minWidth: 0,
      ...Platform.select({
        web: {
          gap: tableColumnGap,
          columnGap: tableColumnGap,
        },
        default: {
          gap: tableColumnGap,
        },
      }),
    },
    evenRow: {
      backgroundColor: colors.backgroundPrimary,
    },
    cell: {
      fontSize: 14,
      color: colors.textPrimary,
      textAlign: 'left',
      justifyContent: 'center',
    },
    cellText: {
      fontSize: 14,
      color: colors.textPrimary,
      lineHeight: 20,
      ...Platform.select({
        web: {
          whiteSpace: 'nowrap' as any,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        default: {
          numberOfLines: 1,
        },
      }),
    },
    /**
     * #1575 FR-5: the unread state, as weight and nothing else.
     *
     * Both halves are written down rather than one of them being "whatever the
     * base style already does": the seen state is a decision (`fontWeight.regular`
     * exists to say exactly that), and an explicit pair is what lets a test tell
     * "drawn as seen" apart from "the style never applied".
     *
     * Shared by the web table's customer / project cells and the card's title, so
     * the two renderers emphasise with the same weight by construction. No new
     * colour, background, column or icon — the row chrome is unchanged.
     */
    /**
     * ⚠️ THE WEIGHT MARKS UNREAD; THE GROUND MOVED THE OTHER WAY (owner, 2026-10-07).
     *
     * The approved design (deck slide 4) was "unread = bold on a WHITE row,
     * read = body weight on the shade". On 2026-10-06 the whole treatment was
     * inverted, and on 2026-10-07 the weight was put back where it started
     * while the GROUND stayed swapped. The shipped combination is therefore:
     *
     *   unread  →  semibold, on {@link colors.surfaceRowListUnread} (the shade)
     *   read    →  body weight, on white
     *
     * Both halves now point the same way again — "unread" picks the text style
     * AND the surface — so there is one expression per renderer, not two.
     */
    unreadRowText: {
      fontWeight: fontWeight.semibold,
    },
    readRowText: {
      fontWeight: fontWeight.regular,
    },
    /**
     * The ground half of the Gmail treatment (#1575 FR-5, design deck slide 4:
     * "bold on a white row … regular weight on the light shade"). The weight
     * above is carried per text cell; this is carried by the row (web) and the
     * card (mobile), so every column moves together.
     *
     * ⚠️ IT MUST SIT AFTER `evenRow` IN THE STYLE ARRAY. Both set
     * `backgroundColor`, and the unread/read ground is the one that must win.
     */
    unreadRowSurface: {
      backgroundColor: colors.surfaceRowListUnread,
    },
    readRowSurface: {
      backgroundColor: colors.backgroundPrimary,
    },
    clickableCell: {
      justifyContent: 'center',
    },
    requestIdColumn: {
      flex: isWeb ? 1.8 : 1,
      minWidth: isWeb ? 140 : undefined,
    },
    requestIdContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      justifyContent: 'flex-start',
    },
    // Ticket #808: Styles for subtle Request ID positioning
    tableRowWithSubtleId: {
      position: 'relative',
      paddingTop: 16, // Keep row height at 64px
    },
    subtleRequestIdContainer: {
      position: 'absolute',
      top: 2,
      left: 8,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    subtleRequestId: {
      fontSize: 14,
      color: colors.textMuted,
      fontWeight: '400',
    },
    nameColumn: {
      flex: isWeb ? 1.6 : 1,
      minWidth: isWeb ? 120 : undefined,
    },
    modifiedByColumn: {
      flex: isWeb ? 1.7 : 1,
      minWidth: isWeb ? 100 : undefined,
    },
    /** Single-line ellipsis on web; max width from layout.viewRequests.modifiedByMaxWidthCh */
    modifiedByDisplayText: {
      fontSize: 14,
      lineHeight: 20,
      letterSpacing: 0.25,
      ...Platform.select({
        web: {
          width: '100%',
          maxWidth: `${vrLayout.modifiedByMaxWidthCh}ch`,
          whiteSpace: 'nowrap' as const,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        default: {},
      }),
    },
    modifiedByTableCellColor: {
      color: colors.textPrimary,
    },
    regionColumn: {
      flex: isWeb ? 1 : 1,
      minWidth: isWeb ? 80 : undefined,
    },
    dateColumn: {
      flex: isWeb ? 1.4 : 1,
      minWidth: isWeb ? 100 : undefined,
    },
    stageColumn: {
      flex: isWeb ? 1.2 : 1,
      minWidth: isWeb ? 80 : undefined,
    },
    actionsColumn: {
      flex: isWeb ? 1 : 1,
      // The floor is the row's content (review 2026-09-11; re-derived
      // 2026-09-23 when the dot left the flow): the marker and Copy are each a
      // glyph inside `actionButton`'s padding, and `actionButtons` puts 8 px
      // between them. The unread dot overhangs the marker's glyph and is out of
      // flow, so it adds nothing here. `flex: 1` alone shares out less than
      // this at the low end of every tier, and a cell narrower than its content
      // spills left over the Stage badge. The header cell is empty; nothing
      // else sets it.
      minWidth: isWeb ? ACTIONS_COLUMN_MIN_WIDTH : undefined,
    },
    rightAlignHeader: {
      textAlign: 'right',
    },
    rightAlignCell: {
      alignItems: 'flex-end',
      justifyContent: 'center',
    },
    stageBadgeWrapper: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      justifyContent: 'flex-end',
    },
    actionButtons: {
      flexDirection: 'row',
      alignItems: 'center',
      // The same `spacing.sm` ACTIONS_COLUMN_MIN_WIDTH reserves (code review P8;
      // this was a literal 8, true only by coincidence).
      gap: spacing.sm,
    },
    actionButton: {
      padding: TABLE_ACTION_PADDING,
      borderRadius: 6,
      backgroundColor: 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: {
          cursor: 'pointer',
          ':hover': {
            backgroundColor: 'transparent',
          },
        },
      }),
    },
    stageBadge: {
      backgroundColor: '#ffeaa7',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 12,
      minWidth: 60,
      alignItems: 'center',
    },
    stageText: {
      fontSize: 12,
      color: '#8b6914',
      fontWeight: '600',
    },
    paginationWrapper: {
      alignSelf: 'stretch',
      alignItems: 'center',
    },
    paginationContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 20,
      paddingTop: 20,
      paddingHorizontal: shouldShowTable ? 0 : 16,
      borderTopWidth: 1,
      borderTopColor: '#ddd',
      width: '100%',
      maxWidth: 1200,
    },
    paginationInfo: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    paginationControls: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    paginationLabel: {
      fontSize: 14,
      color: '#666',
    },
    pageButton: {
      backgroundColor: '#fff',
      borderRadius: 4,
      borderWidth: 1,
      borderColor: '#ddd',
      paddingHorizontal: 16,
      paddingVertical: 8,
    },
    pageButtonDisabled: {
      opacity: 0.5,
    },
    pageButtonText: {
      fontSize: 14,
      color: '#333',
      fontWeight: '500',
    },
    pageButtonTextDisabled: {
      color: '#999',
    },
    pageInfo: {
      fontSize: 14,
      color: '#666',
      marginHorizontal: 8,
    },
    paginationText: {
      fontSize: 14,
      color: '#666',
    },
    pageSizeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      position: 'relative',
      zIndex: 1,
    },
    pageSizeDropdown: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#fff',
      borderWidth: 1,
      borderColor: '#ddd',
      borderRadius: 4,
      paddingHorizontal: 12,
      paddingVertical: 6,
    },
    pageSizeText: {
      fontSize: 14,
      color: '#333',
      fontWeight: '500',
    },
    pageSizeOptions: {
      position: 'absolute',
      bottom: 36,
      right: 0,
      backgroundColor: '#fff',
      borderWidth: 1,
      borderColor: '#ddd',
      borderRadius: 4,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 8,
      zIndex: 9999,
      minWidth: 80,
    },
    pageSizeOption: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#f0f0f0',
      minHeight: 44,
      justifyContent: 'center',
    },
    pageSizeOptionActive: {
      backgroundColor: '#fff8f4',
    },
    pageSizeOptionText: {
      fontSize: 14,
      color: '#333',
      textAlign: 'center',
    },
    pageSizeOptionTextActive: {
      color: '#ff6b35',
      fontWeight: '500',
    },
    cardsContainer: {
      width: '100%',
      maxWidth: shouldShowTable ? undefined : 1200,
    },
    cardsGrid: {
      flexDirection: isTablet || isMobileLandscape ? 'row' : 'column',
      flexWrap: isTablet || isMobileLandscape ? 'wrap' : 'nowrap',
      gap: isTablet ? 16 : isMobileLandscape ? 12 : 0,
      width: '100%',
      justifyContent: isMobileLandscape ? 'space-between' : 'flex-start',
      paddingHorizontal: 0,
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: 8,
      marginBottom: isTablet || isMobileLandscape ? 0 : 12,
      borderWidth: 1,
      borderColor: '#e0e0e0',
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.05,
      shadowRadius: 3,
      elevation: 2,
      width: isTablet || isMobileLandscape ? '48%' : '100%',
      maxWidth: isTablet ? 350 : undefined,
      padding: 16,
      alignSelf: isTablet || isMobileLandscape ? undefined : 'stretch',
      // Add hover effect for web
      ...Platform.select({
        web: {
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          ':hover': {
            transform: 'translateY(-1px)',
            shadowOpacity: 0.08,
            shadowRadius: 4,
          },
        },
      }),
    },
    cardTopRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    cardRequestId: {
      fontSize: 18,
      fontWeight: '600',
      color: '#1a1a1a',
      lineHeight: 22,
    },
    // Ticket #808: Subtle, de-emphasized Request ID for mobile cards
    cardRequestIdSubtle: {
      fontSize: 14,
      fontWeight: '400',
      color: '#999',
      lineHeight: 18,
    },
    cardRequestIdContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    cardCustomerName: {
      fontSize: 16,
      fontWeight: '400',
      color: '#1a1a1a',
      marginBottom: 4,
    },
    cardProjectName: {
      fontSize: 14,
      fontWeight: '500',
      color: '#ff6b35',
      marginBottom: 12,
    },
    tableProjectName: {
      fontSize: 12,
      fontWeight: '500',
      color: '#ff6b35',
      marginTop: 2,
    },
    cardTopActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    stageBadgeContainer: {
      alignSelf: 'center',
    },
    cardActionIcon: {
      padding: CARD_ACTION_PADDING,
      borderRadius: 4,
      backgroundColor: 'transparent',
    },
    cardContent: {
      gap: 12,
    },
    cardInfoGrid: {
      flexDirection: 'row',
      gap: 16,
    },
    cardInfoItem: {
      flex: 1,
      minWidth: 0,
    },
    cardInfoLabel: {
      fontSize: 11,
      color: '#666',
      fontWeight: '600',
      marginBottom: 4,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    cardInfoValue: {
      fontSize: 14,
      color: '#1a1a1a',
      fontWeight: '400',
    },
    cardFooter: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginTop: isTablet ? 8 : 4,
    },
    cardStageBadge: {
      backgroundColor: '#007AFF',
      paddingHorizontal: isTablet ? 16 : 12,
      paddingVertical: isTablet ? 8 : 6,
      borderRadius: isTablet ? 20 : 16,
      alignItems: 'center',
      minWidth: isTablet ? 80 : 70,
    },
    cardStageText: {
      fontSize: isTablet ? 13 : 12,
      color: '#fff',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 60,
    },
    loadingText: {
      fontSize: 16,
      color: '#666',
      marginTop: 16,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 60,
      paddingHorizontal: 20,
    },
    errorTitle: {
      fontSize: 20,
      fontWeight: '600',
      color: '#333',
      marginTop: 16,
      marginBottom: 8,
    },
    errorMessage: {
      fontSize: 16,
      color: '#666',
      textAlign: 'center',
      marginBottom: 24,
      lineHeight: 22,
    },
    retryButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#007bff',
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: 6,
      gap: 8,
    },
    retryButtonText: {
      fontSize: 16,
      color: '#fff',
      fontWeight: '500',
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 60,
      paddingHorizontal: 20,
    },
    emptyTitle: {
      fontSize: 20,
      fontWeight: '600',
      color: '#333',
      marginBottom: 8,
    },
    emptyMessage: {
      fontSize: 16,
      color: '#666',
      textAlign: 'center',
      lineHeight: 22,
    },
    offlineCard: {
      borderLeftWidth: 4,
      borderLeftColor: '#ffc107',
      backgroundColor: '#fffbf0',
      opacity: 0.8,
    },
    cardIdRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    offlineBadge: {
      backgroundColor: '#FF9500',
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 8,
    },
    offlineBadgeText: {
      fontSize: 10,
      color: '#fff',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    // Modal overlay for native platforms
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalContent: {
      width: '90%',
      maxWidth: 400,
      maxHeight: '80%',
    },
    dropdownContainer: {
      position: 'absolute',
      top: 50,
      left: 0,
      zIndex: 999999,
      marginTop: 4,
      minWidth: 280,
      maxWidth: 320,
    },
    dropdown: {
      backgroundColor: '#fff',
      borderRadius: 12,
      borderWidth: 1,
      borderColor: '#ddd',
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.15,
      shadowRadius: 12,
      elevation: 8,
      maxHeight: 500,
    },
    dropdownHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#eee',
    },
    dropdownTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: '#333',
    },
    clearFiltersText: {
      fontSize: 14,
      color: '#ff6b35',
      fontWeight: '500',
    },
    dropdownContent: {
      paddingVertical: 8,
    },
    dropdownSection: {
      paddingHorizontal: 16,
      paddingVertical: 8,
    },
    dropdownSectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    dropdownSectionLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: '#666',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    clearSectionText: {
      fontSize: 12,
      color: '#ff6b35',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    dropdownOptions: {
      gap: 4,
    },
    dropdownOption: {
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 6,
    },
    dropdownOptionActive: {
      backgroundColor: '#fff8f4',
    },
    dropdownOptionText: {
      fontSize: 14,
      color: '#333',
    },
    dropdownOptionTextActive: {
      color: '#ff6b35',
      fontWeight: '500',
    },
    sortDirectionText: {
      fontSize: 12,
      color: '#666',
      fontWeight: '400',
    },
    privacyFooterContainer: {
      alignSelf: 'stretch',
      alignItems: 'center',
      marginTop: 20,
      marginBottom: 20,
      paddingHorizontal: 20,
    },
    privacyLinkContainer: {
      alignItems: 'flex-start',
      marginTop: 20,
      paddingHorizontal: shouldShowTable ? 0 : 16,
      width: '100%',
      maxWidth: 1200,
    },
    privacyLinkText: {
      fontSize: 14,
      color: '#333',
      ...Platform.select({
        web: {
          cursor: 'pointer',
          ':hover': {
            color: '#666',
          },
        },
      }),
    },
    confidentialContainer: {
      marginTop: 16,
      paddingHorizontal: shouldShowTable ? 0 : 16,
      paddingVertical: 8,

      width: '100%',
      maxWidth: 1200,
    },
    confidentialText: {
      fontSize: 12,
      color: '#666',
      lineHeight: 18,
      textAlign: 'justify',
    },
    supportLinkText: {
      fontSize: 12,
      color: '#007bff',
      textDecorationLine: 'underline',
      ...Platform.select({
        web: {
          cursor: 'pointer',
          ':hover': {
            color: '#0056b3',
          },
        },
      }),
    },
  });
};
